// frontend/src/pages/ProjectPage.tsx
import { useState, useEffect, useCallback, useRef, useMemo, ReactNode, ChangeEvent } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Modal } from '../components/Modal';
import { LoadingSpinner, PageLoader } from '../components/LoadingSpinner';
import { InsufficientCreditsModal } from '../components/InsufficientCreditsModal';
import CodeEditor from '../components/CodeEditor';
import FrontendPreview, { EMBEDDING_BATCH_LIMITS, MINIAPP_MAX_PAYLOAD_BYTES } from '../components/FrontendPreview';
import { ReportProblemButton } from '../components/ReportProblemButton';
import { useProject, useConversations, useProjectCode, useMessages, FormDataEntry } from '../hooks/useApi';
import { useTurnstile } from '../hooks/useTurnstile';
import { useRunCostStats } from '../hooks/useRunCostStats';
import { useUsage } from '../hooks/useUsage';
import { useModelCreditCosts } from '../hooks/useModelCreditCosts';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { MiniAppType, MonetizationMode } from '../lib/supabase';
import { VISIBLE_AGENTIC_CODER_MODELS, type VisibleAgenticCoderModelId } from '../lib/agentModels';
import { dbSelect, invokeFunction } from '../lib/api';
import { PAYMENTS_DISABLED, PAYMENTS_DISABLED_MESSAGE } from '../lib/payments';
import {
  ArrowLeft,
  MessageSquare,
  Plus,
  Settings,
  Globe,
  Lock,
  Trash2,
  Copy,
  Check,
  ExternalLink,
  AlertCircle,
  Crown,
  Sparkles,
  Loader2,
  GitFork,
  DollarSign,
  ChevronDown,
  ChevronUp,
  Clock,
  Users,
  ShieldCheck,
  Send,
  User,
  Code,
  RefreshCw,
  RefreshCcw,
  Square,
  Wand2,
  BarChart3,
  Cpu,
  Paperclip,
  X,
  Layers,
} from 'lucide-react';

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Detect whether the index.html is still the factory-default "File Processor" template */
function isDefaultHtml(html: string): boolean {
  return html.includes('<title>File Processor</title>') && html.includes('📁 File Processor');
}

function formatMemoryAsGi(memorySetting: string | null): string {
  if (!memorySetting) return '';
  const normalized = memorySetting.trim().toLowerCase();
  const match = normalized.match(/^(\d+(?:\.\d+)?)(mi|gi)$/);
  if (!match) return memorySetting;

  const value = Number.parseFloat(match[1]);
  if (!Number.isFinite(value) || value <= 0) return memorySetting;

  if (match[2] === 'gi') return `${match[1]}Gi`;
  const gi = value / 1024;
  const rounded = Number.parseFloat(gi.toFixed(3));
  return `${Number.isInteger(rounded) ? Math.trunc(rounded) : rounded}Gi`;
}

const FALLBACK_LOGO_EMOJI = '✨';
const LOGO_EMOJI_REGEX = /(?:\p{Extended_Pictographic}|\p{Regional_Indicator})/u;
const MAX_CHAT_ATTACHMENTS = 8;
const MAX_EMBEDDED_FRONTEND_FILE_CHARS = 200000;

function normalizeLogoEmoji(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';

  try {
    const SegmenterCtor = (Intl as unknown as {
      Segmenter?: new (_locale?: string, options?: { granularity: 'grapheme' }) => {
        segment: (input: string) => Iterable<{ segment: string }>;
      };
    }).Segmenter;
    if (SegmenterCtor) {
      const segmenter = new SegmenterCtor(undefined, { granularity: 'grapheme' });
      for (const part of segmenter.segment(trimmed)) {
        return part.segment;
      }
    }
  } catch {
    // Fall back to simple code-point extraction.
  }

  return Array.from(trimmed)[0] ?? '';
}

function isLogoEmojiSet(value: string): boolean {
  return LOGO_EMOJI_REGEX.test(value.trim());
}

function mergeUniqueStrings(base: string[], additions: string[]): string[] {
  const merged = new Set(base);
  additions.forEach((item) => {
    if (item) merged.add(item);
  });
  return Array.from(merged);
}

function splitMessageContentAndAttachments(content: string): { mainContent: string; attachmentNames: string[] } {
  const attachmentRegex = /\n?\n?(\[Attached: [^\]]+\]\n?)+$/;
  const attachmentMatch = content.match(attachmentRegex);
  if (!attachmentMatch) {
    return { mainContent: content, attachmentNames: [] };
  }

  const mainContent = content.slice(0, attachmentMatch.index).trimEnd();
  const attachmentNames: string[] = [];
  const nameRegex = /\[Attached: ([^\]]+)\]/g;
  let m;
  while ((m = nameRegex.exec(attachmentMatch[0])) !== null) {
    attachmentNames.push(m[1]);
  }

  return { mainContent, attachmentNames };
}

/** Render message text with placeholder links (shared with ConversationPage) */
function renderMessageContent(content: string): ReactNode {
  const placeholders: { pattern: string; link: ReactNode }[] = [
    {
      pattern: '{{UPGRADE_LINK}}',
      link: PAYMENTS_DISABLED
        ? <span key="upgrade" className="text-theme-text-muted">{PAYMENTS_DISABLED_MESSAGE}</span>
        : <Link key="upgrade" to="/pricing" className="text-accent-500 hover:text-accent-600 underline font-medium">View subscription plans</Link>,
    },
    {
      pattern: '{{GUIDE_LINK}}',
      link: <Link key="guide" to="/guide" className="text-accent-500 hover:text-accent-600 underline font-medium">User Guide</Link>,
    },
  ];

  // Process placeholders in main content
  const hasPlaceholder = placeholders.some(p => content.includes(p.pattern));
  let textNode: ReactNode = content;
  if (hasPlaceholder) {
    const parts: ReactNode[] = [];
    let remaining = content;
    let keyIndex = 0;

    while (remaining.length > 0) {
      let earliestIndex = remaining.length;
      let matchedPlaceholder: (typeof placeholders)[0] | null = null;

      for (const placeholder of placeholders) {
        const index = remaining.indexOf(placeholder.pattern);
        if (index !== -1 && index < earliestIndex) {
          earliestIndex = index;
          matchedPlaceholder = placeholder;
        }
      }

      if (matchedPlaceholder && earliestIndex < remaining.length) {
        if (earliestIndex > 0) {
          parts.push(<span key={keyIndex++}>{remaining.slice(0, earliestIndex)}</span>);
        }
        parts.push(<span key={keyIndex++}>{matchedPlaceholder.link}</span>);
        remaining = remaining.slice(earliestIndex + matchedPlaceholder.pattern.length);
      } else {
        parts.push(<span key={keyIndex++}>{remaining}</span>);
        break;
      }
    }
    textNode = <>{parts}</>;
  }

  return textNode;
}

// ─── Collapsible message bubble ──────────────────────────────────────────

const MESSAGE_COLLAPSE_HEIGHT = 120; // px threshold to collapse

function CollapsibleMessage({
  content,
  isUser,
  isExpanded,
  onToggle,
}: {
  content: string;
  isUser: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const contentRef = useRef<HTMLPreElement>(null);
  const [needsCollapse, setNeedsCollapse] = useState(false);
  const { mainContent, attachmentNames } = useMemo(
    () => splitMessageContentAndAttachments(content),
    [content]
  );

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    // Use ResizeObserver to detect when content height exceeds threshold
    const observer = new ResizeObserver(() => {
      setNeedsCollapse(el.scrollHeight > MESSAGE_COLLAPSE_HEIGHT);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [content]);

  const isCollapsed = needsCollapse && !isExpanded;

  return (
    <div>
      <div
        className="relative"
        style={isCollapsed ? { maxHeight: MESSAGE_COLLAPSE_HEIGHT + 'px', overflow: 'hidden' } : undefined}
      >
        <pre ref={contentRef} className="whitespace-pre-wrap text-left font-sans">
          {renderMessageContent(mainContent)}
        </pre>
        {isCollapsed && (
          <div
            className={'absolute bottom-0 left-0 right-0 h-10 pointer-events-none ' +
              (isUser
                ? 'bg-gradient-to-t from-accent-500 via-accent-500/80 to-transparent'
                : 'bg-gradient-to-t from-theme-surface via-theme-surface/80 to-transparent')
            }
          />
        )}
      </div>
      {attachmentNames.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {attachmentNames.map((name, i) => (
            <span
              key={i}
              className={
                'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] leading-tight ' +
                (isUser
                  ? 'bg-white/20 text-white/90'
                  : 'bg-theme-surface-hover text-theme-text-secondary')
              }
            >
              <Paperclip className="h-2.5 w-2.5 flex-shrink-0" />
              {name}
            </span>
          ))}
        </div>
      )}
      {needsCollapse && (
        <button
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
          className={'flex items-center gap-1 mt-1 text-[10px] font-semibold transition-colors ' +
            (isUser
              ? 'text-white hover:text-white/80'
              : 'text-theme-text-secondary hover:text-theme-text')
          }
        >
          {isExpanded ? (
            <><ChevronUp className="h-3 w-3" />Show less</>
          ) : (
            <><ChevronDown className="h-3 w-3" />Show more</>
          )}
        </button>
      )}
    </div>
  );
}

// ─── Model selector ─────────────────────────────────────────────────────────

const MODELS = VISIBLE_AGENTIC_CODER_MODELS;

type ModelId = VisibleAgenticCoderModelId;

const MODEL_STORAGE_KEY = 'llambada-selected-model';

function getStoredModel(): ModelId {
  const stored = localStorage.getItem(MODEL_STORAGE_KEY);
  if (stored && MODELS.some(m => m.id === stored)) return stored as ModelId;
  return 'kimi-for-coding';
}

function setStoredModel(modelId: ModelId) {
  localStorage.setItem(MODEL_STORAGE_KEY, modelId);
}

// ─── Component ──────────────────────────────────────────────────────────────

export function ProjectPage() {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const { isAdmin, isPremiumUser, subscriptionTier, session, user, profile, updateProfile, authReady } = useAuth();
  const robotEmail = 'robot@llambada.com';
  const isRobotUser =
    (user?.email || '').toLowerCase() === robotEmail ||
    (profile?.email || '').toLowerCase() === robotEmail;
  const isPrivilegedProjectViewer = isAdmin || isRobotUser;

  // Turnstile for bot protection
  const { getToken: getTurnstileToken, TurnstileWidget } = useTurnstile();

  const {
    project,
    loading: projectLoading,
    streaming,
    updateProject,
    runMiniApp,
    resetStreaming,
    refetch: refetchProject,
  } = useProject(projectId, getTurnstileToken);
  const hasProjectToolAccess = !!project && !!user && (isPrivilegedProjectViewer || project.user_id === user.id);
  const canDeleteProject = !!project && !!user && project.user_id === user.id;
  const isFamilyForkProject = !!project?.forked_from && !!project?.family_params;
  const {
    conversations,
    loading: conversationsLoading,
    createConversation,
    updateConversation,
    deleteConversation,
  } = useConversations(projectId, { enabled: hasProjectToolAccess });
  const { files: codeFiles, loading: codeLoading, fetchCode } = useProjectCode(projectId, { enabled: hasProjectToolAccess });

  // Run cost stats
  const { stats: runCostStats, loading: costStatsLoading } = useRunCostStats(projectId, 7, { enabled: hasProjectToolAccess });
  const [showStatsModal, setShowStatsModal] = useState(false);

  // Credit pre-check for mini-app runs
  const { data: usageData, loading: usageLoading } = useUsage();
  const modelCreditCosts = useModelCreditCosts();
  const { locale } = useI18n();
  const [avgDailyCredits, setAvgDailyCredits] = useState<number | null>(null);
  const [showCreditsModal, setShowCreditsModal] = useState(false);

  // Fetch average daily credits per user for this mini-app
  useEffect(() => {
    if (!projectId) return;
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    fetch(`${supabaseUrl}/rest/v1/rpc/get_avg_daily_credits_per_user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': anonKey,
      },
      body: JSON.stringify({ p_project_id: projectId }),
    })
      .then(res => res.json())
      .then(data => {
        if (typeof data === 'number') {
          setAvgDailyCredits(Math.max(4, Math.round(data)));
        } else {
          setAvgDailyCredits(4);
        }
      })
      .catch(() => setAvgDailyCredits(4));
  }, [projectId]);

  // Frontend HTML
  const frontendHtml = codeFiles['frontend/index.html'] || '';
  const hasFrontend = !!frontendHtml;
  const htmlIsDefault = hasFrontend && isDefaultHtml(frontendHtml);

  // Frontend files (memoized)
  const frontendFiles = useMemo(() => {
    const files: Record<string, string> = {};
    for (const [path, content] of Object.entries(codeFiles)) {
      if (path.startsWith('frontend/') && path !== 'frontend/index.html' && path !== 'frontend/common.css') {
        if (typeof content === 'string' && content.length > MAX_EMBEDDED_FRONTEND_FILE_CHARS) {
          continue;
        }
        const filename = path.replace('frontend/', '');
        files[filename] = content;
      }
    }
    return files;
  }, [codeFiles]);

  // ─── Inline chat state ──────────────────────────────────────────────────

  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [builderMode, setBuilderMode] = useState(false); // true once the user has submitted their first message or the HTML is non-default
  const [transitionPhase, setTransitionPhase] = useState<'idle' | 'sliding' | 'done'>('idle');
  const [initialPrompt, setInitialPrompt] = useState('');
  const [initialPromptLocked, setInitialPromptLocked] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatAttachments, setChatAttachments] = useState<File[]>([]);
  const [selectedModel, setSelectedModel] = useState<ModelId>(getStoredModel);
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [resubmittingMessageId, setResubmittingMessageId] = useState<string | null>(null);
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(new Set());

  // useMessages for the active conversation
  const {
    messages,
    loading: messagesLoading,
    sending,
    error: chatError,
    streamingContent,
    streamingStatus,
    sendMessage,
    stopLatestRun,
    resubmitMessage,
    lastModifiedFiles,
    clearLastModifiedFiles,
    buildStarted,
    clearBuildStarted,
  } = useMessages(activeConversationId ?? undefined, projectId);

  const chatInputRef = useRef<HTMLTextAreaElement>(null);
  const chatAttachmentInputRef = useRef<HTMLInputElement>(null);
  const initialInputRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const previewPanelRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const chatPanelRef = useRef<HTMLDivElement>(null);
  const previewCardRef = useRef<HTMLDivElement>(null);
  const previewWrapperRef = useRef<HTMLDivElement>(null);
  const [previewHeight, setPreviewHeight] = useState<number>(0);
  const [reportButtonPos, setReportButtonPos] = useState<{ bottom: number; right: number }>({
    bottom: 12,
    right: 12,
  });
  const submittedFromCompactRef = useRef(false);
  const switchingConversationRef = useRef(false);
  const initialPromptSubmittedRef = useRef(false);
  const pendingAutoSendAttachmentsRef = useRef<File[] | null>(null);

  const toggleMessageExpand = useCallback((messageId: string) => {
    setExpandedMessages(prev => {
      const next = new Set(prev);
      if (next.has(messageId)) next.delete(messageId);
      else next.add(messageId);
      return next;
    });
  }, []);

  const switchToConversation = useCallback((conversationId: string) => {
    switchingConversationRef.current = true;
    setActiveConversationId(conversationId);
    setExpandedMessages(new Set());
    // Scroll the chat panel into view (useful when clicking from the history list below)
    setTimeout(() => {
      chatPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, []);

  // ─── Existing project-page state ─────────────────────────────────────────

  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showSpecificationModal, setShowSpecificationModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);
  const [showProjectDeleteModal, setShowProjectDeleteModal] = useState(false);
  const [showPublishUpgradeModal, setShowPublishUpgradeModal] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [projectSpecification, setProjectSpecification] = useState('');
  const [projectLogoEmoji, setProjectLogoEmoji] = useState('');
  const [projectType, setProjectType] = useState<MiniAppType>('utility');
  const [adminTypeOverride, setAdminTypeOverride] = useState<MiniAppType | null>(null);
  const [isPublished, setIsPublished] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [monetizationMode, setMonetizationMode] = useState<MonetizationMode>('free');
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletingProject, setDeletingProject] = useState(false);
  const [deleteFamilyRecord, setDeleteFamilyRecord] = useState(false);
  const [deleteProjectError, setDeleteProjectError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [rebuildError, setRebuildError] = useState<string | null>(null);
  const [parentRebuildStatus, setParentRebuildStatus] = useState<'idle' | 'pending' | 'building' | 'done' | 'error'>('idle');
  const [parentRebuildError, setParentRebuildError] = useState<string | null>(null);
  const [buildLogs, setBuildLogs] = useState<string | null>(null);
  const [buildLogsLoading, setBuildLogsLoading] = useState(false);
  const [buildLogsError, setBuildLogsError] = useState<string | null>(null);
  const [buildLogsDiagnostics, setBuildLogsDiagnostics] = useState<{
    steps: string[];
    logsBucket?: string | null;
    gcsAttempted: boolean;
    gcsSuccess: boolean;
    gcsError?: string;
    cloudLoggingAttempted: boolean;
    cloudLoggingSuccess: boolean;
    cloudLoggingError?: string;
  } | null>(null);
  const [isImageSizeLimitError, setIsImageSizeLimitError] = useState(false);
  const [showImageSizeLimitModal, setShowImageSizeLimitModal] = useState(false);
  const [generatingDescription, setGeneratingDescription] = useState(false);
  const [generatingLogoEmoji, setGeneratingLogoEmoji] = useState(false);
  const [logoEmojiChoices, setLogoEmojiChoices] = useState<string[]>([]);
  const [seenGeneratedLogoEmojis, setSeenGeneratedLogoEmojis] = useState<string[]>([]);
  const [otherPublishedCount, setOtherPublishedCount] = useState<number | null>(null);
  const [otherPublishedLoading, setOtherPublishedLoading] = useState(false);
  const [settingsToast, setSettingsToast] = useState<{
    message: string;
    tone: 'warning' | 'error' | 'success';
  } | null>(null);

  // Error modal state
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [pendingErrorMessage, setPendingErrorMessage] = useState<string | null>(null);
  const [pendingErrorType, setPendingErrorType] = useState<'execution' | 'build'>('execution');
  const [errorHandoffInProgress, setErrorHandoffInProgress] = useState(false);
  const lastHandledErrorRef = useRef<string | null>(null);
  const lastHandledBuildErrorRef = useRef<string | null>(null);

  const buildLogsRef = useRef<string | null>(null);
  const settingsToastTimeoutRef = useRef<number | null>(null);
  useEffect(() => {
    buildLogsRef.current = buildLogs;
  }, [buildLogs]);

  useEffect(() => {
    if (project?.name) {
      document.title = `${project.name} - Llambada`;
    }
    return () => { document.title = 'Llambada'; };
  }, [project?.name]);

  const hasUnlimitedImageSize = isAdmin || isRobotUser;
  const imageSizeLimitLabel = hasUnlimitedImageSize
    ? 'Unlimited'
    : subscriptionTier === 'premium'
      ? '2GB'
      : (subscriptionTier === 'standard' || subscriptionTier === 'standard_with_ads')
        ? '1GB'
        : '500MB';

  const isFreeUser = subscriptionTier === 'free';

  const fetchOtherPublishedCount = useCallback(async () => {
    if (!isFreeUser || !user || !session || !projectId) {
      setOtherPublishedCount(0);
      return 0;
    }

    setOtherPublishedLoading(true);
    try {
      const { count, error } = await dbSelect('projects', {
        token: session.access_token,
        filters: {
          user_id: `eq.${user.id}`,
          is_published: 'eq.true',
          id: `neq.${projectId}`,
        },
        count: 'exact',
        head: true,
      });

      if (error) {
        console.warn('Failed to fetch published mini-app count:', error);
        setOtherPublishedCount(0);
        return 0;
      }

      const normalizedCount = count ?? 0;
      setOtherPublishedCount(normalizedCount);
      return normalizedCount;
    } finally {
      setOtherPublishedLoading(false);
    }
  }, [isFreeUser, user, session, projectId]);

  useEffect(() => {
    if (!showSettingsModal) return;
    fetchOtherPublishedCount();
  }, [showSettingsModal, fetchOtherPublishedCount]);

  useEffect(() => {
    if (isFreeUser && monetizationMode !== 'free') {
      setMonetizationMode('free');
    }
  }, [isFreeUser, monetizationMode]);

  const clearSettingsToastTimeout = useCallback(() => {
    if (settingsToastTimeoutRef.current !== null) {
      window.clearTimeout(settingsToastTimeoutRef.current);
      settingsToastTimeoutRef.current = null;
    }
  }, []);

  const showSettingsToast = useCallback((message: string, tone: 'warning' | 'error' | 'success' = 'warning') => {
    clearSettingsToastTimeout();
    setSettingsToast({ message, tone });
    settingsToastTimeoutRef.current = window.setTimeout(() => {
      setSettingsToast(null);
      settingsToastTimeoutRef.current = null;
    }, 3500);
  }, [clearSettingsToastTimeout]);

  useEffect(() => {
    return () => {
      clearSettingsToastTimeout();
    };
  }, [clearSettingsToastTimeout]);

  // Local build status override
  const [localBuildStatus, setLocalBuildStatus] = useState<string | null>(null);
  const effectiveBuildStatus = localBuildStatus || project?.build_status;

  // ─── Determine builder mode on mount / code change ───────────────────────

  useEffect(() => {
    if (codeLoading) return;
    // If the HTML has been modified from the default template, enter builder mode
    if (hasFrontend && !htmlIsDefault) {
      setBuilderMode(true);
      setTransitionPhase('done');
    }
  }, [codeLoading, hasFrontend, htmlIsDefault]);

  // Auto-send pending message (stored in sessionStorage) after conversation creation
  useEffect(() => {
    if (activeConversationId && session && authReady && messages.length === 0 && !sending && !messagesLoading) {
      const storageKey = 'pendingMessage_' + activeConversationId;
      const pendingMessage = sessionStorage.getItem(storageKey);
      if (pendingMessage) {
        sessionStorage.removeItem(storageKey);
        setTimeout(() => {
          const pendingAttachments = pendingAutoSendAttachmentsRef.current || undefined;
          pendingAutoSendAttachmentsRef.current = null;
          sendMessage(pendingMessage, selectedModel, pendingAttachments);
        }, 500);
      }
    }
  }, [activeConversationId, session, authReady, messages.length, sending, messagesLoading, sendMessage, selectedModel]);

  // Refresh code files and project data after agent finishes
  const prevSendingRef = useRef(sending);
  useEffect(() => {
    const wasSending = prevSendingRef.current;
    prevSendingRef.current = sending;
    if (!sending && wasSending) {
      fetchCode();
      setUploadError(null);
      resetStreaming();
      // Refetch project to pick up build_status changes (e.g. when REBUILD_BACKEND
      // triggered a build). The DB is already updated by the time the SSE stream ends,
      // so this reliably reflects the current build state even if the SSE build_started
      // event was missed due to buffering/timing.
      refetchProject();
    }
  }, [sending, fetchCode, resetStreaming, refetchProject]);

  // Handle build_started event from SSE
  useEffect(() => {
    if (buildStarted) {
      setLocalBuildStatus('building');
      clearBuildStarted();
    }
  }, [buildStarted, clearBuildStarted]);

  // Check if dependencies were modified → set building status
  useEffect(() => {
    if (lastModifiedFiles.length > 0) {
      const dependencyFiles = ['Dockerfile', 'requirements.txt', 'cloud-run.json'];
      const dependenciesModified = lastModifiedFiles.some(file =>
        dependencyFiles.some(dep => file === dep || file.endsWith('/' + dep))
      );
      if (dependenciesModified) {
        setLocalBuildStatus('building');
        clearLastModifiedFiles();
      }
    }
  }, [lastModifiedFiles, clearLastModifiedFiles]);

  // Close model dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setModelDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-resize chat textarea
  useEffect(() => {
    const textarea = chatInputRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const lineHeight = 24;
      const maxRows = 8;
      const maxHeight = lineHeight * maxRows;
      const newHeight = Math.min(textarea.scrollHeight, maxHeight);
      textarea.style.height = newHeight + 'px';
      textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
    }
  }, [chatInput]);

  useEffect(() => {
    setChatAttachments([]);
    // NOTE: Do NOT clear pendingAutoSendAttachmentsRef here.
    // When creating a new conversation, handleChatSubmit stores attachments in the ref
    // and then sets activeConversationId. This effect fires before the auto-send effect
    // (line ~611) can consume the ref, because auto-send is gated by !messagesLoading
    // which is true while useMessages fetches for the new conversation. The ref is
    // properly consumed and cleared inside the auto-send effect itself.
  }, [activeConversationId]);

  // Track the actual preview card height via the ref passed into FrontendPreview
  useEffect(() => {
    const el = previewCardRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      setPreviewHeight(el.offsetHeight);
    });
    observer.observe(el);
    setPreviewHeight(el.offsetHeight);
    return () => observer.disconnect();
  });

  // Position the "Report a problem" button inside the iframe's .container
  useEffect(() => {
    const updateReportButtonPos = () => {
      const wrapper = previewWrapperRef.current;
      if (!wrapper) return;
      const wrapperRect = wrapper.getBoundingClientRect();

      const iframe = wrapper.querySelector('iframe') as HTMLIFrameElement | null;
      if (!iframe) return;
      const iframeRect = iframe.getBoundingClientRect();

      const iframeDoc = iframe.contentDocument;
      if (!iframeDoc) return;

      const appCard = iframeDoc.querySelector('.container') as HTMLElement | null;
      if (!appCard) return;

      const cardRect = appCard.getBoundingClientRect();
      if (cardRect.width === 0 || cardRect.height === 0) return;

      const inset = 12;
      const cardTopInWrapper = (iframeRect.top - wrapperRect.top) + cardRect.top;
      const cardRightInWrapper = (iframeRect.left - wrapperRect.left) + cardRect.right;
      const cardBottomInWrapper = cardTopInWrapper + cardRect.height;

      const nextBottom = Math.max(0, (wrapperRect.height - cardBottomInWrapper) + inset);
      const nextRight = Math.max(0, (wrapperRect.width - cardRightInWrapper) + inset);

      setReportButtonPos((prev) => {
        if (Math.abs(prev.bottom - nextBottom) < 1 && Math.abs(prev.right - nextRight) < 1) {
          return prev;
        }
        return { bottom: nextBottom, right: nextRight };
      });
    };

    updateReportButtonPos();
    const intervalId = window.setInterval(updateReportButtonPos, 500);
    window.addEventListener('resize', updateReportButtonPos);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('resize', updateReportButtonPos);
    };
  }, [frontendHtml]);

  // Auto-scroll to bottom when messages change or streaming
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages, streamingContent, sending]);

  // ─── Build logs / status polling (unchanged from original) ───────────────

  const fetchBuildLogs = useCallback(async () => {
    if (!projectId || !session) {
      if (!session) setBuildLogsError('Not authenticated');
      return;
    }
    setBuildLogsLoading(true);
    setBuildLogsError(null);
    setBuildLogsDiagnostics(null);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(
        supabaseUrl + '/functions/v1/get-build-logs?projectId=' + projectId,
        { headers: { Authorization: 'Bearer ' + session.access_token } }
      );
      if (!response.ok) {
        const error = await response.json();
        setBuildLogsError(error.error || 'Failed to fetch build logs');
        if (error.diagnostics) setBuildLogsDiagnostics(error.diagnostics);
        return;
      }
      const data = await response.json();
      const isPlaceholder = data.logs?.includes('Logs will appear here once the build produces output');
      const currentLogs = buildLogsRef.current;
      const existingHasContent = currentLogs && !currentLogs.includes('Logs will appear here');
      if (isPlaceholder && existingHasContent) {
        // keep existing
      } else if (data.logs) {
        const shouldUpdate = !isPlaceholder || !currentLogs || data.logs.length > currentLogs.length;
        if (shouldUpdate) setBuildLogs(data.logs);
      }
      if (data.diagnostics) setBuildLogsDiagnostics(data.diagnostics);
      if (data.isImageSizeLimitError) setIsImageSizeLimitError(true);
    } catch (err) {
      setBuildLogsError(err instanceof Error ? err.message : 'Failed to fetch build logs');
    } finally {
      setBuildLogsLoading(false);
    }
  }, [projectId, session]);

  const triggerRebuild = useCallback(async () => {
    if (!projectId || !session) return;
    setRebuildError(null);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      setBuildLogs(null);
      setBuildLogsDiagnostics(null);
      setIsImageSizeLimitError(false);
      setShowImageSizeLimitModal(false);
      setLocalBuildStatus('pending');
      const response = await fetch(supabaseUrl + '/functions/v1/trigger-project-build', {
        method: 'POST',
        headers: {
          Authorization: 'Bearer ' + session.access_token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ projectId, force: true }),
      });
      const data = await response.json();
      if (!response.ok) {
        setRebuildError(data.error || 'Rebuild failed (' + response.status + ')');
        setLocalBuildStatus(null);
        return;
      }
      if (!data.buildId && data.buildStatus !== 'building') {
        setRebuildError(data.message || 'Build was not triggered');
        setLocalBuildStatus(null);
        return;
      }
      await refetchProject();
      setLocalBuildStatus('building');
    } catch (err) {
      setRebuildError(err instanceof Error ? err.message : 'Network error triggering rebuild');
      setLocalBuildStatus(null);
    }
  }, [projectId, session, refetchProject]);

  const triggerParentRebuild = useCallback(async () => {
    if (!project?.forked_from || !session) return;
    setParentRebuildError(null);
    setParentRebuildStatus('pending');
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(supabaseUrl + '/functions/v1/trigger-project-build', {
        method: 'POST',
        headers: {
          Authorization: 'Bearer ' + session.access_token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ projectId: project.forked_from, force: true }),
      });
      const data = await response.json();
      if (!response.ok) {
        setParentRebuildError(data.error || 'Parent rebuild failed (' + response.status + ')');
        setParentRebuildStatus('error');
        return;
      }
      if (!data.buildId && data.buildStatus !== 'building') {
        setParentRebuildError(data.message || 'Parent build was not triggered');
        setParentRebuildStatus('error');
        return;
      }
      setParentRebuildStatus('building');
      // Poll for completion
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await fetch(supabaseUrl + '/functions/v1/check-build-status?projectId=' + project.forked_from, {
            headers: { Authorization: 'Bearer ' + session.access_token },
          });
          if (statusRes.ok) {
            const statusData = await statusRes.json();
            if (statusData.buildStatus === 'ready') {
              clearInterval(pollInterval);
              setParentRebuildStatus('done');
              setTimeout(() => setParentRebuildStatus('idle'), 3000);
            } else if (statusData.buildStatus === 'failed') {
              clearInterval(pollInterval);
              setParentRebuildError('Parent build failed');
              setParentRebuildStatus('error');
            }
          }
        } catch { /* ignore poll errors */ }
      }, 5000);
      // Safety: stop polling after 10 minutes
      setTimeout(() => clearInterval(pollInterval), 600000);
    } catch (err) {
      setParentRebuildError(err instanceof Error ? err.message : 'Network error triggering parent rebuild');
      setParentRebuildStatus('error');
    }
  }, [project?.forked_from, session]);

  const prevDbBuildStatus = useRef(project?.build_status);
  useEffect(() => {
    const dbStatus = project?.build_status;
    const prevStatus = prevDbBuildStatus.current;
    prevDbBuildStatus.current = dbStatus;
    if (prevStatus !== dbStatus) {
      // Only clear localBuildStatus when the build is done in the DB.
      // Do NOT clear it when the DB transitions to 'building' — that causes a race where
      // localBuildStatus is nulled before project?.build_status is confirmed 'building',
      // making effectiveBuildStatus briefly drop to 'ready' and hiding the modal.
      if (dbStatus === 'ready' && prevStatus === 'building') setLocalBuildStatus(null);
    }
  }, [project?.build_status, localBuildStatus]);

  useEffect(() => {
    if (effectiveBuildStatus !== 'building' || !projectId || !session) return;
    const checkBuildStatus = async () => {
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const response = await fetch(supabaseUrl + '/functions/v1/check-build-status?projectId=' + projectId, {
          headers: { Authorization: 'Bearer ' + session.access_token },
        });
        if (response.ok) {
          const data = await response.json();
          if (data.buildStatus === 'ready' || data.buildStatus === 'failed') {
            setLocalBuildStatus(null);
            refetchProject();
            fetchBuildLogs();
            if (data.isImageSizeLimitError) {
              setIsImageSizeLimitError(true);
              setShowImageSizeLimitModal(true);
            }
          }
        }
      } catch (err) {
        console.warn('Error checking build status:', err);
      }
    };
    const initialDelay = setTimeout(checkBuildStatus, 3000);
    const interval = setInterval(checkBuildStatus, 5000);
    return () => { clearTimeout(initialDelay); clearInterval(interval); };
  }, [effectiveBuildStatus, projectId, refetchProject, fetchBuildLogs]);

  useEffect(() => {
    if (effectiveBuildStatus !== 'building' || !projectId) return;
    const initialDelay = setTimeout(() => fetchBuildLogs(), 5000);
    const interval = setInterval(() => fetchBuildLogs(), 3000);
    return () => { clearTimeout(initialDelay); clearInterval(interval); };
  }, [effectiveBuildStatus, projectId, fetchBuildLogs]);

  // ─── Error handling (run log errors) ─────────────────────────────────────

  useEffect(() => {
    if (streaming.status === 'authenticating' || streaming.status === 'running') {
      lastHandledErrorRef.current = null;
    }
  }, [streaming.status]);

  useEffect(() => {
    if (sending || creating || errorHandoffInProgress) return;
    if (streaming.status !== 'completed' && streaming.status !== 'failed') return;
    const logs = streaming.logs;
    if (!logs) return;
    const hasError = logs.exitCode !== 0 || logs.hasAppError;
    if (!hasError) return;
    let errorDetails = '';
    if (logs.stderr) errorDetails += 'stderr:\n' + logs.stderr + '\n\n';
    if (logs.stdout) errorDetails += 'stdout:\n' + logs.stdout + '\n\n';
    if (logs.exitCode !== 0) errorDetails += 'Exit code: ' + logs.exitCode;
    const errorMessage = 'The mini-app encountered an execution error:\n\n' + errorDetails.trim() + '\n\nPlease help me fix this issue.';
    if (lastHandledErrorRef.current === errorMessage) return;
    setPendingErrorMessage(errorMessage);
    setPendingErrorType('execution');
    setShowErrorModal(true);
  }, [streaming.status, streaming.logs, sending, creating, errorHandoffInProgress]);

  // Detect build failures and show the error modal with build logs
  useEffect(() => {
    if (effectiveBuildStatus !== 'failed' || !projectId || !session || sending || creating || errorHandoffInProgress) return;
    const buildErrorKey = `build-failed-${project?.last_build_id || projectId}`;
    if (lastHandledBuildErrorRef.current === buildErrorKey) return;
    lastHandledBuildErrorRef.current = buildErrorKey;

    let cancelled = false;

    (async () => {
      let errorDetails = 'The backend build failed.';
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const response = await fetch(
          `${supabaseUrl}/functions/v1/get-build-logs?projectId=${projectId}`,
          { headers: { Authorization: `Bearer ${session.access_token}` } }
        );
        if (response.ok) {
          const data = await response.json();
          if (data.logs) {
            errorDetails = `The backend Docker build failed. Build logs:\n\n${data.logs}`;
          }
        }
      } catch (_) {}
      const errorMessage = `${errorDetails}\n\nPlease help me fix this build error.`;
      if (cancelled) return;
      setPendingErrorMessage(errorMessage);
      setPendingErrorType('build');
      setShowErrorModal(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [effectiveBuildStatus, projectId, session, project?.last_build_id, sending, creating, errorHandoffInProgress]);

  useEffect(() => {
    if (sending || creating || errorHandoffInProgress) {
      setShowErrorModal(false);
    }
  }, [sending, creating, errorHandoffInProgress]);

  // Reset build error ref when a new build starts
  useEffect(() => {
    if (effectiveBuildStatus === 'building') {
      lastHandledBuildErrorRef.current = null;
    }
  }, [effectiveBuildStatus]);

  // ─── Settings handlers (unchanged) ──────────────────────────────────────

  const closeSettingsModal = () => {
    setShowSpecificationModal(false);
    setShowSettingsModal(false);
  };

  const handleOpenSettings = () => {
    if (project) {
      setProjectName(project.name);
      setProjectDescription(project.description || '');
      setProjectSpecification(project.specification || '');
      setProjectLogoEmoji(project.logo_emoji || '');
      setProjectType(project.type || 'utility');
      setAdminTypeOverride(project.admin_type_override || null);
      setIsPublished(project.is_published);
      setDisplayName(profile?.display_name || '');
      setMonetizationMode((project.monetization_mode as MonetizationMode) || 'free');
      setLogoEmojiChoices([]);
      setSeenGeneratedLogoEmojis([]);
      setSettingsToast(null);
      clearSettingsToastTimeout();
      setShowSpecificationModal(false);
      setShowSettingsModal(true);
    }
  };

  const detectRequiresAuth = (files: Record<string, string>): boolean => {
    const allCode = Object.values(files).join('\n');
    const patterns = [
      /app-data/i, /app_user_data/i,
      /app-files/i, /app_user_files/i,
      /app-vectors/i, /app_user_vectors/i,
      /generate-embeddings/i,
      /llambada\.\s*(?:get|set|delete|list|clear)Data/,
      /llambada\.\s*(?:set|get|delete|list|search|deleteVector)Vector/,
      /llambada\.\s*vectorStats/,
      /llambada\.\s*storageInfo/,
      /llambada\.\s*(?:upload|download)File/,
      /callDataApi|callVectorApi|callFileApi/,
    ];
    return patterns.some(pattern => pattern.test(allCode));
  };

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    const needsNickname = isPublished && !isAdmin && !profile?.display_name;
    if (needsNickname && !displayName.trim()) return;

    const normalizedLogoEmoji = normalizeLogoEmoji(projectLogoEmoji);
    if (normalizedLogoEmoji && !isLogoEmojiSet(normalizedLogoEmoji)) {
      showSettingsToast('Logo emoji must contain an emoji character.', 'warning');
      return;
    }
    if (isPublished && !isLogoEmojiSet(normalizedLogoEmoji)) {
      showSettingsToast('Set the Logo emoji first before publishing this mini-app.', 'warning');
      return;
    }

    setSaving(true);
    if (needsNickname && displayName.trim()) {
      await updateProfile({ display_name: displayName.trim() });
    }
    const requiresAuth = detectRequiresAuth(codeFiles);
    const effectiveMonetizationMode = isFreeUser ? 'free' : monetizationMode;
    const newRequiredTier = effectiveMonetizationMode === 'paid_only' ? 'standard_with_ads' : null;
    const typeIsLocked = !!adminTypeOverride && !isAdmin;
    const updateResult = await updateProject({
      name: projectName,
      description: projectDescription,
      specification: projectSpecification.trim() ? projectSpecification : null,
      logo_emoji: normalizedLogoEmoji || null,
      ...(typeIsLocked ? {} : { type: projectType }),
      is_published: isPublished,
      required_tier: newRequiredTier,
      monetization_mode: effectiveMonetizationMode,
      requires_auth: requiresAuth,
    });
    if (updateResult.error) {
      setSaving(false);
      showSettingsToast(updateResult.error, 'error');
      return;
    }
    if (projectId && session?.access_token) {
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        await fetch(supabaseUrl + '/functions/v1/update-miniapp-embedding', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
          body: JSON.stringify({ projectId }),
        });
      } catch (err) {
        console.warn('Failed to update miniapp embedding:', err);
      }
    }
    setProjectLogoEmoji(normalizedLogoEmoji);
    setSaving(false);
    closeSettingsModal();
  };

  const generateDescription = async () => {
    if (!frontendHtml || !session) return;
    setGeneratingDescription(true);
    try {
      const titleForGeneration = projectName.trim() || project?.name || '';
      const { data, error } = await invokeFunction<{ description: string; type?: MiniAppType }>(
        'generate-description',
        { html: frontendHtml, title: titleForGeneration },
        { token: session.access_token }
      );
      if (!error && data?.description) setProjectDescription(data.description);
      const typeIsLocked = !!adminTypeOverride && !isAdmin;
      if (!error && data?.type && !typeIsLocked) setProjectType(data.type);
    } catch (err) {
      console.error('Failed to generate description:', err);
    } finally {
      setGeneratingDescription(false);
    }
  };

  const generateLogoEmoji = async () => {
    if (!session) return;
    setGeneratingLogoEmoji(true);

    try {
      const appName = projectName.trim() || project?.name || 'Mini-app';

      const { data: publishedLogos, error: publishedLogoError } = await dbSelect<Array<{ logo_emoji: string | null }>>(
        'projects',
        {
          token: session.access_token,
          select: 'logo_emoji',
          filters: {
            is_published: 'eq.true',
            logo_emoji: 'not.is.null',
          },
        }
      );

      if (publishedLogoError) {
        throw publishedLogoError;
      }

      const usedEmojis = new Set(
        (publishedLogos || [])
          .map((item) => normalizeLogoEmoji(item.logo_emoji || ''))
          .filter((emoji) => isLogoEmojiSet(emoji))
      );

      const seenSet = new Set(seenGeneratedLogoEmojis);
      const nextSeenSet = new Set(seenSet);

      // Use semantic search via the emoji-picker mini-app backend (no credits deducted)
      const { data, error } = await invokeFunction<{ emojis?: string[] }>(
        'generate-description',
        {
          action: 'emoji_search',
          query: appName,
          topK: 100,
        },
        { token: session.access_token }
      );

      if (error) {
        throw error;
      }

      if (!error && Array.isArray(data?.emojis)) {
        const normalizedResults = data.emojis
          .map((emoji) => normalizeLogoEmoji(emoji))
          .filter((emoji) => isLogoEmojiSet(emoji));

        const available = normalizedResults.filter(
          (emoji) => !usedEmojis.has(emoji) && !nextSeenSet.has(emoji)
        );

        if (available.length > 0) {
          const choices = Array.from(new Set(available)).slice(0, 100);
          choices.forEach((emoji) => nextSeenSet.add(emoji));
          setLogoEmojiChoices(choices);
          setSeenGeneratedLogoEmojis(Array.from(nextSeenSet));
          showSettingsToast('Choose a logo emoji from the generated options.', 'success');
          return;
        }
      }

      setProjectLogoEmoji(FALLBACK_LOGO_EMOJI);
      setLogoEmojiChoices([FALLBACK_LOGO_EMOJI]);
      setSeenGeneratedLogoEmojis(mergeUniqueStrings(seenGeneratedLogoEmojis, [FALLBACK_LOGO_EMOJI]));
      showSettingsToast('No emoji found. Set to ✨.', 'warning');
    } catch (err) {
      console.error('Failed to generate logo emoji:', err);
      setProjectLogoEmoji(FALLBACK_LOGO_EMOJI);
      setLogoEmojiChoices([FALLBACK_LOGO_EMOJI]);
      setSeenGeneratedLogoEmojis(mergeUniqueStrings(seenGeneratedLogoEmojis, [FALLBACK_LOGO_EMOJI]));
      showSettingsToast('Failed to generate logo emoji, defaulted to ✨.', 'error');
    } finally {
      setGeneratingLogoEmoji(false);
    }
  };

  // ─── Conversation handlers ──────────────────────────────────────────────

  const handleCreateConversation = async () => {
    setCreating(true);
    const result = await createConversation();
    setCreating(false);
    if (result.data) {
      setActiveConversationId(result.data.id);
    }
  };

  const handleDeleteConversation = async (id: string) => {
    setDeleting(true);
    await deleteConversation(id);
    setDeleting(false);
    setShowDeleteModal(null);
    // If the deleted conversation was active, switch to the next one
    if (id === activeConversationId) {
      const remaining = conversations.filter(c => c.id !== id);
      setActiveConversationId(remaining.length > 0 ? remaining[0].id : null);
    }
  };

  const handleOpenProjectDeleteModal = () => {
    if (!canDeleteProject) return;
    setDeleteProjectError(null);
    setDeleteFamilyRecord(false);
    setShowProjectDeleteModal(true);
  };

  const handleDeleteProject = async () => {
    if (!projectId || !session || !canDeleteProject) return;

    setDeletingProject(true);
    setDeleteProjectError(null);

    const payload: { projectId: string; deleteFamilyCombination?: boolean } = { projectId };
    if (isFamilyForkProject && deleteFamilyRecord) {
      payload.deleteFamilyCombination = true;
    }

    const { data, error } = await invokeFunction<{ success?: boolean; error?: string }>(
      'delete-project-infrastructure',
      payload,
      { token: session.access_token },
    );

    if (error || !data?.success) {
      setDeleteProjectError(error?.message || data?.error || 'Failed to delete project');
      setDeletingProject(false);
      return;
    }

    setDeletingProject(false);
    setShowProjectDeleteModal(false);
    navigate('/dashboard');
  };

  const copyPublicUrl = () => {
    if (project?.public_id) {
      const url = window.location.origin + '/p/' + project.public_id + (project.slug ? '/' + project.slug : '');
      navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const typeIsLocked = !!adminTypeOverride && !isAdmin;
  const effectiveProjectType = adminTypeOverride ?? projectType;
  const monetizationDisabled = isFreeUser;
  const canFreeUserPublish = !isFreeUser || (otherPublishedCount ?? 0) === 0;
  const publishToggleDisabled = isFreeUser && !isPublished && (otherPublishedLoading || otherPublishedCount === null);

  // ─── Chat handlers ──────────────────────────────────────────────────────

  const handleOpenChatAttachmentPicker = () => {
    if (sending || creating) return;
    chatAttachmentInputRef.current?.click();
  };

  const handleChatAttachmentChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = '';
    if (selectedFiles.length === 0) return;

    setChatAttachments((prev) => {
      const next = [...prev];
      for (const file of selectedFiles) {
        if (next.length >= MAX_CHAT_ATTACHMENTS) break;
        const duplicate = next.some(
          (existing) =>
            existing.name === file.name &&
            existing.size === file.size &&
            existing.lastModified === file.lastModified
        );
        if (!duplicate) next.push(file);
      }
      return next;
    });
  };

  const handleRemoveChatAttachment = (index: number) => {
    setChatAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const clearChatAttachments = () => {
    setChatAttachments([]);
  };

  const handleChatSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatInput.trim() || sending || creating) return;
    const content = chatInput.trim();
    const attachmentsToSend = [...chatAttachments];
    setChatInput('');
    setChatAttachments([]);
    if (chatInputRef.current) {
      chatInputRef.current.style.height = '40px';
      chatInputRef.current.style.overflowY = 'hidden';
    }

    // If no active conversation, create one first
    if (!activeConversationId) {
      submittedFromCompactRef.current = true;
      setCreating(true);
      const title = content.slice(0, 50) + (content.length > 50 ? '...' : '');
      const result = await createConversation(title);
      setCreating(false);
      if (result.data) {
        // Store message so it auto-sends after conversation loads
        pendingAutoSendAttachmentsRef.current = attachmentsToSend.length > 0 ? attachmentsToSend : null;
        sessionStorage.setItem('pendingMessage_' + result.data.id, content);
        setActiveConversationId(result.data.id);
      } else if (attachmentsToSend.length > 0) {
        setChatAttachments(attachmentsToSend);
      }
      return;
    }

    const result = await sendMessage(content, selectedModel, attachmentsToSend);
    if (result.error && attachmentsToSend.length > 0) {
      setChatAttachments(attachmentsToSend);
    }
    // Update conversation title if it's the first message
    if (messages.length === 0 && activeConversationId && result.data) {
      const title = content.slice(0, 50) + (content.length > 50 ? '...' : '');
      await updateConversation(activeConversationId, title);
    }
  };

  const handleChatKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleChatSubmit(e);
    }
  };

  const handleModelChange = (modelId: ModelId) => {
    setSelectedModel(modelId);
    setStoredModel(modelId);
    setModelDropdownOpen(false);
  };

  const handleCopyMessage = async (messageId: string, content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleResubmitMessage = async (messageId: string) => {
    if (sending || resubmittingMessageId) return;
    setResubmittingMessageId(messageId);
    try {
      await resubmitMessage(messageId, selectedModel);
    } finally {
      setResubmittingMessageId(null);
    }
  };

  /** User submits the initial prompt (pre-builder mode) */
  const handleInitialSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!initialPrompt.trim() || creating || initialPromptSubmittedRef.current) return;
    initialPromptSubmittedRef.current = true;
    setInitialPromptLocked(true);
    const content = initialPrompt.trim();
    const attachmentsToSend = [...chatAttachments];
    setCreating(true);

    // Create a conversation
    const result = await createConversation(content.slice(0, 50) + (content.length > 50 ? '...' : ''));
    setCreating(false);

    if (result.data) {
      // Store message so it auto-sends after the conversation loads
      pendingAutoSendAttachmentsRef.current = attachmentsToSend.length > 0 ? attachmentsToSend : null;
      setChatAttachments([]);
      sessionStorage.setItem('pendingMessage_' + result.data.id, content);
      setActiveConversationId(result.data.id);

      // Start the slide transition
      setTransitionPhase('sliding');
      setTimeout(() => {
        setBuilderMode(true);
        setTransitionPhase('done');
      }, 600); // match CSS transition duration
    } else {
      // Unlock on failure so the user can try again.
      initialPromptSubmittedRef.current = false;
      setInitialPromptLocked(false);
    }
  };

  const handleInitialKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleInitialSubmit(e);
    }
  };

  // ─── Frontend preview handlers ──────────────────────────────────────────

  const handleFrontendSubmit = async (entries: FormDataEntry[]) => {
    setUploadError(null);

    // Credit pre-check: only block when we have a real credit balance.
    // If usage hasn't loaded yet, defer to the backend checks instead of
    // incorrectly treating the user as having 0 credits.
    if (avgDailyCredits !== null) {
      const creditsRemaining = usageData?.credits?.totalRemaining ?? null;
      if (creditsRemaining !== null && creditsRemaining < avgDailyCredits) {
        setShowCreditsModal(true);
        return;
      }
    }

    const result = await runMiniApp(entries);
    if (result.error) {
      setUploadError(result.error);
      const isBackendBuilding =
        result.errorCode === 'BACKEND_BUILDING' ||
        /Backend is (still being built|currently rebuilding)/i.test(result.error);
      if (!isBackendBuilding && !sending && !creating && !errorHandoffInProgress) {
        const errorMessage = 'The mini-app encountered an execution error:\n\n' + result.error + '\n\nPlease help me fix this issue.';
        setPendingErrorMessage(errorMessage);
        setShowErrorModal(true);
      }
    }
  };

  const handleFrontendError = (errorText: string) => {
    if (sending || creating || errorHandoffInProgress) return;
    const errorMessage = 'The mini-app encountered a frontend JavaScript error:\n\n' + errorText + '\n\nPlease help me fix this issue.';
    setPendingErrorMessage(errorMessage);
    setShowErrorModal(true);
  };

  // Error modal handlers
  const handleGoToConversation = async () => {
    if (!pendingErrorMessage) return;
    const errorMessage = pendingErrorMessage;
    lastHandledErrorRef.current = errorMessage;
    setErrorHandoffInProgress(true);
    setShowErrorModal(false);

    try {
      // If we have an active conversation, auto-send the error there
      if (activeConversationId && !sending) {
        await sendMessage(errorMessage, selectedModel);
        setErrorHandoffInProgress(false);
      } else {
        // Create a new conversation
        setCreating(true);
        const result = await createConversation('Fix error: ' + new Date().toLocaleString());
        setCreating(false);
        if (result.data) {
          sessionStorage.setItem('pendingError_' + result.data.id, errorMessage);
          setActiveConversationId(result.data.id);
        } else {
          setErrorHandoffInProgress(false);
        }
      }
    } catch (_) {
      setErrorHandoffInProgress(false);
    }
    setPendingErrorMessage(null);
  };

  const handleDismissError = () => {
    if (pendingErrorMessage) lastHandledErrorRef.current = pendingErrorMessage;
    setShowErrorModal(false);
    setPendingErrorMessage(null);
    setUploadError(null);
    // Don't resetStreaming() here — keep the logs visible in the backend tab
    // until the user runs the mini-app again (which naturally resets them)
  };

  const handleDismissImageSizeLimitError = () => setShowImageSizeLimitModal(false);

  const handleFixImageSizeLimitError = async () => {
    setShowImageSizeLimitModal(false);
    const errorMessage = (hasUnlimitedImageSize
      ? 'The Docker image for this mini-app exceeds the size limit.'
      : 'The Docker image for this mini-app exceeds the ' + imageSizeLimitLabel + ' size limit.'
    ) + '\n\nHere are the deployment logs showing the error:\n\n```\n' + (buildLogs || 'No logs available') + '\n```\n\nPlease help me reduce the Docker image size.';
    setCreating(true);
    const result = await createConversation('Fix image size: ' + new Date().toLocaleString());
    setCreating(false);
    if (result.data) {
      sessionStorage.setItem('pendingError_' + result.data.id, errorMessage);
      setActiveConversationId(result.data.id);
    }
  };

  // Auto-send pending errors (from error modal flow)
  useEffect(() => {
    if (activeConversationId && session && authReady && messages.length === 0 && !sending && !messagesLoading) {
      const storageKey = 'pendingError_' + activeConversationId;
      const pendingError = sessionStorage.getItem(storageKey);
      if (pendingError) {
        sessionStorage.removeItem(storageKey);
        setTimeout(async () => {
          try {
            await sendMessage(pendingError, selectedModel);
          } finally {
            setErrorHandoffInProgress(false);
          }
        }, 500);
      }
    }
  }, [activeConversationId, session, authReady, messages.length, sending, messagesLoading, sendMessage, selectedModel]);

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Must be before early returns to satisfy Rules of Hooks
  void useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role === 'user') return messages[i].id;
    }
    return null;
  }, [messages]);

  // ─── Loading states ─────────────────────────────────────────────────────

  if (projectLoading) {
    return <Layout><PageLoader /></Layout>;
  }

  if (!project) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <h1 className="text-2xl font-bold text-theme-text mb-4">Mini-App not found</h1>
          <Link to="/dashboard" className="btn btn-primary">Back to Dashboard</Link>
        </div>
      </Layout>
    );
  }

  if (!hasProjectToolAccess) {
    const publicProjectUrl = project.public_id
      ? '/p/' + project.public_id + (project.slug ? '/' + project.slug : '')
      : null;

    return (
      <Layout>
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-8">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 text-sm text-theme-text-muted hover:text-theme-text transition-colors mb-6"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to projects
          </Link>

          <div className="card p-6 sm:p-8">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-6 w-6 text-theme-warning shrink-0 mt-0.5" />
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-theme-text mb-2">Access Restricted</h1>
                <p className="text-theme-text-secondary">
                  You are trying to access another user&apos;s project. Builder tools and project management are available
                  only to the project owner.
                </p>
                {publicProjectUrl && (
                  <div className="mt-4">
                    <Link to={publicProjectUrl} className="btn btn-primary">
                      Open Public Mini-App
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const publicUrl = project.public_id
    ? window.location.origin + '/p/' + project.public_id + (project.slug ? '/' + project.slug : '')
    : '';

  const selectedModelInfo = MODELS.find(m => m.id === selectedModel) || MODELS[0];
  const activeConversation = conversations.find(c => c.id === activeConversationId);

  // Compact chat: show only a textarea until the user has messages (or is sending)
  // submittedFromCompactRef prevents a brief flash back to compact during conversation creation
  // switchingConversationRef prevents flashing to compact when switching between conversations
  if (messages.length > 0 || sending) {
    submittedFromCompactRef.current = false;
    switchingConversationRef.current = false;
  }
  if (!messagesLoading && messages.length === 0 && switchingConversationRef.current) {
    // Loaded an empty conversation — clear the flag so compact mode can show
    switchingConversationRef.current = false;
  }
  const isCompactChat = !submittedFromCompactRef.current && (!activeConversationId || (messages.length === 0 && !sending && !messagesLoading && !switchingConversationRef.current));

  // ═══════════════════════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════════════════════

  return (
    <Layout>
      <div className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="mb-4 sm:mb-6">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 text-sm text-theme-text-muted hover:text-theme-text transition-colors mb-3"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to projects
          </Link>

          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                {project.logo_emoji && (
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-theme-surface text-xl">
                    {project.logo_emoji}
                  </span>
                )}
                <h1 className="font-display text-xl sm:text-2xl font-bold text-theme-text break-words">
                  {project.name}
                </h1>
                {project.forked_from && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-theme-primary/10 text-theme-primary whitespace-nowrap">
                    <GitFork className="h-3 w-3" />Fork
                  </span>
                )}
                {project.is_published ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-theme-success-bg text-theme-success whitespace-nowrap">
                    <Globe className="h-3 w-3" />Published
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-theme-surface text-theme-text-muted whitespace-nowrap">
                    <Lock className="h-3 w-3" />Private
                  </span>
                )}
                {project.is_family_template && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-500/10 text-blue-500 whitespace-nowrap">
                    <Layers className="h-3 w-3" />Family Template
                  </span>
                )}
              </div>
              {project.forked_from && (
                <div className="mt-1 flex items-center gap-3 flex-wrap">
                  <p className="text-xs text-theme-warning flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    This is a forked project. You can modify frontend and backend code, but not requirements.txt or Dockerfile.
                  </p>
                  {isAdmin && (
                    <button
                      onClick={triggerParentRebuild}
                      disabled={parentRebuildStatus === 'pending' || parentRebuildStatus === 'building'}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-theme-primary/10 text-theme-primary hover:bg-theme-primary/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Rebuild the parent project's backend container"
                    >
                      <RefreshCw className={'h-3 w-3' + (parentRebuildStatus === 'pending' || parentRebuildStatus === 'building' ? ' animate-spin' : '')} />
                      {parentRebuildStatus === 'pending' ? 'Queuing...' : parentRebuildStatus === 'building' ? 'Building...' : parentRebuildStatus === 'done' ? 'Done!' : 'Rebuild Parent Backend'}
                    </button>
                  )}
                  {parentRebuildError && (
                    <span className="text-xs text-theme-error">{parentRebuildError}</span>
                  )}
                </div>
              )}
              {project.description && (
                <p className="mt-1.5 text-theme-text-muted text-sm">{project.description}</p>
              )}
              {project.is_published && project.public_id && (
                <div className="mt-2 flex items-center gap-2">
                  <a
                    href={'/p/' + project.public_id + (project.slug ? '/' + project.slug : '')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-accent-400 hover:text-accent-300 bg-theme-surface/50 px-2 py-1 rounded-lg font-mono transition-colors flex items-center gap-1.5 overflow-hidden"
                  >
                    <Globe className="h-3 w-3 shrink-0" />
                    <span className="truncate">{publicUrl}</span>
                    <ExternalLink className="h-3 w-3 shrink-0" />
                  </a>
                  <button onClick={copyPublicUrl} className="btn btn-ghost p-1.5" title="Copy URL">
                    {copied ? <Check className="h-3.5 w-3.5 text-theme-success" /> : <Copy className="h-3.5 w-3.5" />}
                  </button>
                </div>
              )}
              {(isAdmin || isRobotUser) && (
                <div className="mt-2 inline-flex items-center gap-2 rounded-lg border border-theme-border-muted bg-theme-surface/50 px-2.5 py-1.5 text-xs text-theme-text-secondary">
                  <Cpu className="h-3.5 w-3.5 text-accent-400" />
                  <span>
                    Runtime resources:{' '}
                    <span className="font-semibold text-theme-text">{project.cloud_run_cpu || ''}</span>
                    {project.cloud_run_cpu && project.cloud_run_memory ? ' / ' : ''}
                    <span className="font-semibold text-theme-text">{formatMemoryAsGi(project.cloud_run_memory)}</span>
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 self-start shrink-0">
              {project.is_published && (
                <button onClick={() => setShowStatsModal(true)} className="btn btn-secondary" title="Usage Statistics">
                  <BarChart3 className="h-4 w-4" />
                  <span className="hidden sm:inline">Stats</span>
                </button>
              )}
              <button onClick={handleOpenSettings} className="btn btn-secondary">
                <Settings className="h-4 w-4" />
                <span className="hidden sm:inline">Settings</span>
              </button>
            </div>
          </div>
        </div>

        {/* ── Main content: Initial prompt OR 30/70 builder split ──────── */}
        {(!builderMode && transitionPhase === 'idle') ? (
          /* ─── INITIAL STATE: Centered prompt ───────────────────────── */
          <div className="flex flex-col items-center justify-center min-h-[50vh] animate-fade-in">
            <div className="w-full max-w-2xl mx-auto text-center">
              <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-accent-500/20 to-cyan-500/20 mb-6">
                <Wand2 className="h-8 w-8 text-accent-400" />
              </div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-theme-text mb-3">
                What mini-app do you want to build?
              </h2>
              <p className="text-theme-text-muted mb-8 text-sm sm:text-base">
                Describe your idea and the coding AI will build it for you.
              </p>
              <form onSubmit={handleInitialSubmit} className="w-full">
                <input
                  ref={chatAttachmentInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleChatAttachmentChange}
                  disabled={creating || initialPromptLocked || chatAttachments.length >= MAX_CHAT_ATTACHMENTS}
                />
                <div className="relative">
                  <textarea
                    ref={initialInputRef}
                    value={initialPrompt}
                    onChange={(e) => setInitialPrompt(e.target.value)}
                    onKeyDown={handleInitialKeyDown}
                    placeholder='e.g. "A pomodoro timer with task tracking and stats"'
                    rows={3}
                    className={'input w-full py-4 px-5 pr-14 text-base resize-none rounded-xl' + (initialPromptLocked ? ' bg-theme-surface text-theme-text-muted cursor-not-allowed' : '')}
                    disabled={creating || initialPromptLocked}
                    autoFocus
                  />
                  <button
                    type="submit"
                    disabled={!initialPrompt.trim() || creating || initialPromptLocked}
                    className="absolute right-3 bottom-3 p-2.5 rounded-lg bg-accent-500 text-white hover:bg-accent-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {creating ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                  </button>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <div className="relative" ref={dropdownRef}>
                    <button
                      type="button"
                      onClick={() => setModelDropdownOpen(!modelDropdownOpen)}
                      disabled={creating || initialPromptLocked}
                      className="flex items-center gap-1 px-2 py-1 rounded-lg bg-theme-surface border border-theme-border-muted hover:bg-theme-surface-hover transition-colors disabled:opacity-50 text-xs"
                      title="Select model"
                    >
                      <span className="font-medium text-theme-text whitespace-nowrap">
                        {selectedModelInfo.name.includes('Opus') ? 'Opus' : selectedModelInfo.name.includes('Sonnet') ? 'Sonnet' : selectedModelInfo.name.includes('GLM') ? 'GLM' : selectedModelInfo.name.includes('MiniMax') ? 'MiniMax' : 'Kimi'}
                      </span>
                      <ChevronDown className={'h-3 w-3 text-theme-text-muted transition-transform ' + (modelDropdownOpen ? 'rotate-180' : '')} />
                    </button>
                    {modelDropdownOpen && (
                      <div className="absolute bottom-full left-0 mb-1 w-48 rounded-lg bg-theme-surface border border-theme-border-muted shadow-lg overflow-hidden z-10">
                        {MODELS.map((model) => (
                          <button
                            key={model.id}
                            type="button"
                            onClick={() => handleModelChange(model.id)}
                            className={'w-full flex items-center justify-between px-3 py-2 text-left hover:bg-theme-surface-hover transition-colors ' +
                              (selectedModel === model.id ? 'bg-theme-surface-hover' : '')
                            }
                          >
                            <div>
                              <div className="text-xs font-medium text-theme-text">{model.name}</div>
                              <div className="text-[10px] text-theme-text-muted">{model.description}</div>
                            </div>
                            {selectedModel === model.id && <Check className="h-3.5 w-3.5 text-accent-500" />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleOpenChatAttachmentPicker}
                    disabled={creating || initialPromptLocked || chatAttachments.length >= MAX_CHAT_ATTACHMENTS}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-theme-surface border border-theme-border-muted hover:bg-theme-surface-hover transition-colors disabled:opacity-50 text-xs text-theme-text"
                    title="Attach files"
                  >
                    <Paperclip className="h-3 w-3" />
                    <span>Attach</span>
                  </button>
                  <span className="text-[10px] text-theme-text-muted ml-auto">
                    {chatAttachments.length > 0
                      ? `${chatAttachments.length}/${MAX_CHAT_ATTACHMENTS} attached`
                      : 'Press Enter to send'}
                  </span>
                </div>
                {chatAttachments.length > 0 && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {chatAttachments.map((file, index) => (
                      <span
                        key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                        className="inline-flex items-center gap-1 rounded-full bg-theme-surface border border-theme-border-muted px-2 py-0.5 text-[10px] text-theme-text"
                      >
                        <Paperclip className="h-2.5 w-2.5 text-theme-text-muted" />
                        <span className="max-w-[180px] truncate">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveChatAttachment(index)}
                          className="rounded p-0.5 hover:bg-theme-surface-hover"
                          aria-label={`Remove ${file.name}`}
                        >
                          <X className="h-2.5 w-2.5 text-theme-text-muted" />
                        </button>
                      </span>
                    ))}
                    <button
                      type="button"
                      onClick={clearChatAttachments}
                      className="inline-flex items-center rounded-full border border-theme-border-muted px-2 py-0.5 text-[10px] text-theme-text-muted hover:text-theme-text hover:bg-theme-surface"
                    >
                      Clear
                    </button>
                  </div>
                )}
                <p className="text-xs text-theme-text-muted mt-3">Press Enter to send, Shift+Enter for new line</p>
              </form>
            </div>
          </div>
        ) : (
          /* ─── BUILDER STATE: 30/70 split ───────────────────────────── */
          <div
            className="flex flex-col lg:flex-row gap-4"
          >
            {/* ─── Chat panel (30%) ────────────────────────────────────── */}
            <div
              ref={chatPanelRef}
              className={'w-full lg:w-[30%] lg:min-w-[320px] lg:max-w-[420px] flex flex-col bg-theme-bg rounded-xl border border-theme-border-muted' + (isCompactChat ? ' lg:self-start' : ' lg:self-start overflow-hidden') + (transitionPhase === 'sliding' ? ' animate-slide-split' : '')}
              style={!isCompactChat ? { height: previewHeight > 0 ? previewHeight + 'px' : 'calc(100vh - 10rem)', maxHeight: previewHeight > 0 ? previewHeight + 'px' : 'calc(100vh - 10rem)' } : undefined}
            >
              {isCompactChat ? (
                /* ─── COMPACT: Just a textarea ──────────────────────────── */
                <div className="p-3">
                  <form onSubmit={handleChatSubmit} className="space-y-2">
                    <div className="flex items-center gap-2 mb-2">
                      <Wand2 className="h-4 w-4 text-accent-400" />
                      <span className="text-sm font-medium text-theme-text">What would you like to change?</span>
                    </div>
                    <input
                      ref={chatAttachmentInputRef}
                      type="file"
                      multiple
                      className="hidden"
                      onChange={handleChatAttachmentChange}
                      disabled={sending || creating || chatAttachments.length >= MAX_CHAT_ATTACHMENTS}
                    />
                    <div className="relative">
                      <textarea
                        ref={chatInputRef}
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyDown={handleChatKeyDown}
                        placeholder="Describe the modifications you want..."
                        rows={2}
                        className="input w-full py-2.5 pr-10 resize-none text-sm"
                        disabled={sending || creating}
                        autoFocus
                      />
                      {sending ? (
                        <button
                          type="button"
                          onClick={stopLatestRun}
                          className="absolute right-1.5 bottom-2.5 p-1.5 rounded-lg bg-theme-error text-white hover:opacity-90 transition-opacity"
                          title="Stop run"
                        >
                          <Square className="h-3.5 w-3.5" />
                        </button>
                      ) : (
                        <button
                          type="submit"
                          disabled={!chatInput.trim() || creating}
                          className="absolute right-1.5 bottom-2.5 p-1.5 rounded-lg bg-accent-500 text-white hover:bg-accent-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {creating ? <LoadingSpinner size="sm" /> : <Send className="h-3.5 w-3.5" />}
                        </button>
                      )}
                    </div>
                    {/* Model selector row */}
                    <div className="flex items-center gap-2">
                      <div className="relative" ref={dropdownRef}>
                        <button
                          type="button"
                          onClick={() => setModelDropdownOpen(!modelDropdownOpen)}
                          disabled={sending}
                          className="flex items-center gap-1 px-2 py-1 rounded-lg bg-theme-surface border border-theme-border-muted hover:bg-theme-surface-hover transition-colors disabled:opacity-50 text-xs"
                          title="Select model"
                        >
                          <span className="font-medium text-theme-text whitespace-nowrap">
                            {selectedModelInfo.name.includes('Opus') ? 'Opus' : selectedModelInfo.name.includes('Sonnet') ? 'Sonnet' : selectedModelInfo.name.includes('GLM') ? 'GLM' : selectedModelInfo.name.includes('MiniMax') ? 'MiniMax' : 'Kimi'}
                          </span>
                          <ChevronDown className={'h-3 w-3 text-theme-text-muted transition-transform ' + (modelDropdownOpen ? 'rotate-180' : '')} />
                        </button>
                        {modelDropdownOpen && (
                          <div className="absolute bottom-full left-0 mb-1 w-48 rounded-lg bg-theme-surface border border-theme-border-muted shadow-lg overflow-hidden z-10">
                            {MODELS.map((model) => (
                              <button
                                key={model.id}
                                type="button"
                                onClick={() => handleModelChange(model.id)}
                                className={'w-full flex items-center justify-between px-3 py-2 text-left hover:bg-theme-surface-hover transition-colors ' +
                                  (selectedModel === model.id ? 'bg-theme-surface-hover' : '')
                                }
                              >
                                <div>
                                  <div className="text-xs font-medium text-theme-text">{model.name}</div>
                                  <div className="text-[10px] text-theme-text-muted">{model.description}</div>
                                </div>
                                {selectedModel === model.id && <Check className="h-3.5 w-3.5 text-accent-500" />}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={handleOpenChatAttachmentPicker}
                        disabled={sending || creating || chatAttachments.length >= MAX_CHAT_ATTACHMENTS}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-theme-surface border border-theme-border-muted hover:bg-theme-surface-hover transition-colors disabled:opacity-50 text-xs text-theme-text"
                        title="Attach files"
                      >
                        <Paperclip className="h-3 w-3" />
                        <span>Attach</span>
                      </button>
                      <span className="text-[10px] text-theme-text-muted ml-auto">
                        {chatAttachments.length > 0
                          ? `${chatAttachments.length}/${MAX_CHAT_ATTACHMENTS} attached`
                          : 'Press Enter to send'}
                      </span>
                    </div>
                    {chatAttachments.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {chatAttachments.map((file, index) => (
                          <span
                            key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                            className="inline-flex items-center gap-1 rounded-full bg-theme-surface border border-theme-border-muted px-2 py-0.5 text-[10px] text-theme-text"
                          >
                            <Paperclip className="h-2.5 w-2.5 text-theme-text-muted" />
                            <span className="max-w-[140px] truncate">{file.name}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveChatAttachment(index)}
                              className="rounded p-0.5 hover:bg-theme-surface-hover"
                              aria-label={`Remove ${file.name}`}
                            >
                              <X className="h-2.5 w-2.5 text-theme-text-muted" />
                            </button>
                          </span>
                        ))}
                        <button
                          type="button"
                          onClick={clearChatAttachments}
                          className="inline-flex items-center rounded-full border border-theme-border-muted px-2 py-0.5 text-[10px] text-theme-text-muted hover:text-theme-text hover:bg-theme-surface"
                        >
                          Clear
                        </button>
                      </div>
                    )}
                  </form>
                </div>
              ) : (
                /* ─── EXPANDED: Header + messages + input ───────────────── */
                <>
                  {/* Chat header */}
                  <div className="flex items-center justify-between px-3 py-2 bg-theme-surface border-b border-theme-border-muted flex-shrink-0">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <MessageSquare className="h-4 w-4 text-accent-400 shrink-0" />
                      <span className="text-sm font-medium text-theme-text truncate">
                        {activeConversation?.title || 'Conversation'}
                      </span>
                    </div>
                    <button
                      onClick={() => setActiveConversationId(null)}
                      disabled={creating || sending}
                      className="p-1.5 rounded-lg hover:bg-theme-surface-hover transition-colors text-theme-text-muted hover:text-theme-text"
                      title="New conversation"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Messages area — grows naturally, scrolls when panel hits max-height */}
                  <div ref={messagesContainerRef} className="overflow-y-auto p-3 space-y-3" style={{ flex: '1 1 0px', minHeight: 0 }}>
                    {messagesLoading ? (
                      <div className="flex justify-center py-8"><LoadingSpinner size="sm" /></div>
                    ) : (
                      <>
                        {messages.map((message) => (
                          <div
                            key={message.id}
                            className={'flex gap-2 ' + (message.role === 'user' ? 'flex-row-reverse' : '')}
                          >
                            <div
                              className={'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg ' +
                                (message.role === 'user' ? 'bg-accent-500' : 'bg-theme-surface')
                              }
                            >
                              {message.role === 'user'
                                ? <User className="h-3.5 w-3.5 text-white" />
                                : <Code className="h-3.5 w-3.5 text-theme-text-secondary" />
                              }
                            </div>
                            <div className={'flex-1 max-w-[85%] ' + (message.role === 'user' ? 'text-right' : '')}>
                              <div
                                className={'inline-block rounded-2xl px-3 py-2 text-xs ' +
                                  (message.role === 'user'
                                    ? 'bg-accent-500 text-white rounded-tr-md'
                                    : 'bg-theme-surface text-theme-text rounded-tl-md')
                                }
                              >
                                <CollapsibleMessage
                                  content={message.content}
                                  isUser={message.role === 'user'}
                                  isExpanded={expandedMessages.has(message.id)}
                                  onToggle={() => toggleMessageExpand(message.id)}
                                />
                              </div>
                              <div className={'flex items-center gap-1.5 mt-1 ' + (message.role === 'user' ? 'justify-end' : '')}>
                                {message.role === 'user' && (
                                  <button
                                    onClick={() => handleResubmitMessage(message.id)}
                                    disabled={sending || resubmittingMessageId !== null}
                                    className="p-0.5 rounded hover:bg-theme-surface-hover transition-colors disabled:opacity-50"
                                    title="Resubmit"
                                  >
                                    {resubmittingMessageId === message.id
                                      ? <RefreshCw className="h-3 w-3 text-theme-text-muted animate-spin" />
                                      : <RefreshCcw className="h-3 w-3 text-theme-text-muted" />
                                    }
                                  </button>
                                )}
                                <button
                                  onClick={() => handleCopyMessage(message.id, message.content)}
                                  className="p-0.5 rounded hover:bg-theme-surface-hover transition-colors"
                                  title="Copy"
                                >
                                  {copiedMessageId === message.id
                                    ? <Check className="h-3 w-3 text-theme-success" />
                                    : <Copy className="h-3 w-3 text-theme-text-muted" />
                                  }
                                </button>
                                <span className="text-[10px] text-theme-text-muted">
                                  {new Date(message.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}

                        {/* Streaming response */}
                        {sending && (
                          <div className="flex gap-2">
                            <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-theme-surface">
                              <Code className="h-3.5 w-3.5 text-theme-text-secondary" />
                            </div>
                            <div className="flex-1">
                              {streamingStatus && (
                                <div className="flex items-center gap-2 mb-1.5 text-xs text-theme-text-muted">
                                  <RefreshCw className="h-3 w-3 animate-spin" />
                                  <span>{streamingStatus}</span>
                                </div>
                              )}
                              {streamingContent ? (
                                <div className="bg-theme-surface rounded-2xl rounded-tl-md px-3 py-2">
                                  <pre className="whitespace-pre-wrap font-sans text-xs text-theme-text">{streamingContent}</pre>
                                </div>
                              ) : !streamingStatus && (
                                <div className="bg-theme-surface rounded-2xl rounded-tl-md px-3 py-2">
                                  <div className="flex gap-1.5">
                                    <span className="h-2 w-2 rounded-full bg-theme-text-muted animate-pulse-soft" />
                                    <span className="h-2 w-2 rounded-full bg-theme-text-muted animate-pulse-soft animate-delay-200" />
                                    <span className="h-2 w-2 rounded-full bg-theme-text-muted animate-pulse-soft animate-delay-400" />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {chatError && (
                          <div className="flex items-center gap-2 p-2 rounded-lg bg-theme-error-bg border border-theme-error-border text-theme-error text-xs">
                            <AlertCircle className="h-4 w-4 flex-shrink-0" />
                            <p>{chatError}</p>
                          </div>
                        )}
                        <div ref={messagesEndRef} />
                      </>
                    )}
                  </div>

                  {/* Chat input */}
                  <div className="border-t border-theme-border-muted p-2.5 flex-shrink-0">
                    <input
                      ref={chatAttachmentInputRef}
                      type="file"
                      multiple
                      className="hidden"
                      onChange={handleChatAttachmentChange}
                      disabled={sending || creating || chatAttachments.length >= MAX_CHAT_ATTACHMENTS}
                    />
                    <form onSubmit={handleChatSubmit} className="flex gap-2 items-start">
                      {/* Model selector */}
                      <div className="relative" ref={dropdownRef}>
                        <button
                          type="button"
                          onClick={() => setModelDropdownOpen(!modelDropdownOpen)}
                          disabled={sending}
                          className="flex items-center gap-1 px-2 py-2 h-9 rounded-lg bg-theme-surface border border-theme-border-muted hover:bg-theme-surface-hover transition-colors disabled:opacity-50 text-xs"
                          title="Select model"
                        >
                          <span className="font-medium text-theme-text whitespace-nowrap">
                            {selectedModelInfo.name.includes('Opus') ? 'Opus' : selectedModelInfo.name.includes('Sonnet') ? 'Sonnet' : selectedModelInfo.name.includes('GLM') ? 'GLM' : selectedModelInfo.name.includes('MiniMax') ? 'MiniMax' : 'Kimi'}
                          </span>
                          <ChevronDown className={'h-3 w-3 text-theme-text-muted transition-transform ' + (modelDropdownOpen ? 'rotate-180' : '')} />
                        </button>
                        {modelDropdownOpen && (
                          <div className="absolute bottom-full left-0 mb-1 w-48 rounded-lg bg-theme-surface border border-theme-border-muted shadow-lg overflow-hidden z-10">
                            {MODELS.map((model) => (
                              <button
                                key={model.id}
                                type="button"
                                onClick={() => handleModelChange(model.id)}
                                className={'w-full flex items-center justify-between px-3 py-2 text-left hover:bg-theme-surface-hover transition-colors ' +
                                  (selectedModel === model.id ? 'bg-theme-surface-hover' : '')
                                }
                              >
                                <div>
                                  <div className="text-xs font-medium text-theme-text">{model.name}</div>
                                  <div className="text-[10px] text-theme-text-muted">{model.description}</div>
                                </div>
                                {selectedModel === model.id && <Check className="h-3.5 w-3.5 text-accent-500" />}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={handleOpenChatAttachmentPicker}
                        disabled={sending || creating || chatAttachments.length >= MAX_CHAT_ATTACHMENTS}
                        className="flex items-center justify-center h-9 w-9 rounded-lg bg-theme-surface border border-theme-border-muted hover:bg-theme-surface-hover transition-colors disabled:opacity-50"
                        title="Attach files"
                      >
                        <Paperclip className="h-3.5 w-3.5 text-theme-text-muted" />
                      </button>

                      <div className="flex-1 relative">
                        <textarea
                          ref={chatInputRef}
                          value={chatInput}
                          onChange={(e) => setChatInput(e.target.value)}
                          onKeyDown={handleChatKeyDown}
                          placeholder="Describe the changes..."
                          rows={1}
                          className="input py-2 pr-9 resize-none text-sm w-full"
                          style={{ minHeight: '36px', lineHeight: '20px' }}
                          disabled={sending || creating}
                        />
                        {sending ? (
                          <button
                            type="button"
                            onClick={stopLatestRun}
                            className="absolute right-1.5 top-1 p-1.5 rounded-lg bg-theme-error text-white hover:opacity-90 transition-opacity"
                            title="Stop run"
                          >
                            <Square className="h-3.5 w-3.5" />
                          </button>
                        ) : (
                          <button
                            type="submit"
                            disabled={!chatInput.trim() || creating}
                            className="absolute right-1.5 top-1 p-1.5 rounded-lg bg-accent-500 text-white hover:bg-accent-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {creating ? <LoadingSpinner size="sm" /> : <Send className="h-3.5 w-3.5" />}
                          </button>
                        )}
                      </div>
                    </form>
                    {chatAttachments.length > 0 && (
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        {chatAttachments.map((file, index) => (
                          <span
                            key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                            className="inline-flex items-center gap-1 rounded-full bg-theme-surface border border-theme-border-muted px-2 py-0.5 text-[10px] text-theme-text"
                          >
                            <Paperclip className="h-2.5 w-2.5 text-theme-text-muted" />
                            <span className="max-w-[180px] truncate">{file.name}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveChatAttachment(index)}
                              className="rounded p-0.5 hover:bg-theme-surface-hover"
                              aria-label={`Remove ${file.name}`}
                            >
                              <X className="h-2.5 w-2.5 text-theme-text-muted" />
                            </button>
                          </span>
                        ))}
                        <button
                          type="button"
                          onClick={clearChatAttachments}
                          className="inline-flex items-center rounded-full border border-theme-border-muted px-2 py-0.5 text-[10px] text-theme-text-muted hover:text-theme-text hover:bg-theme-surface"
                        >
                          Clear
                        </button>
                        <span className="text-[10px] text-theme-text-muted ml-auto">
                          {chatAttachments.length}/{MAX_CHAT_ATTACHMENTS} attached
                        </span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* ─── Preview panel (70%) ──────────────────────────────────── */}
            <div ref={previewPanelRef} className={'w-full lg:flex-1 flex flex-col' + (transitionPhase === 'sliding' ? ' animate-fade-in-right' : '')}>
              {(codeLoading || !authReady) ? (
                <div className="bg-theme-bg rounded-xl border border-theme-border-muted overflow-hidden flex-1">
                  <div className="flex items-center gap-2 px-3 py-2 bg-theme-surface border-b border-theme-border-muted">
                    <Loader2 className="h-4 w-4 text-accent-400 animate-spin" />
                    <span className="text-sm font-medium text-theme-text-secondary">Preview</span>
                  </div>
                  <div className="h-[400px] lg:h-full flex items-center justify-center bg-theme-surface">
                    <div className="text-center">
                      <Loader2 className="h-8 w-8 text-theme-text-muted animate-spin mx-auto mb-3" />
                      <p className="text-theme-text-muted text-sm">Loading the mini-app...</p>
                    </div>
                  </div>
                </div>
              ) : (hasFrontend && !htmlIsDefault) ? (
                <div ref={previewWrapperRef} className="flex-1 flex flex-col relative">
                  <FrontendPreview
                    html={frontendHtml}
                    frontendFiles={frontendFiles}
                    onSubmit={handleFrontendSubmit}
                    isProcessing={streaming.status === 'authenticating' || streaming.status === 'running'}
                    isComplete={streaming.status === 'completed'}
                    backendError={streaming.status === 'failed' ? (streaming.error || 'Unknown error') : null}
                    buildStatus={effectiveBuildStatus}
                    runLogs={streaming.logs}
                    runStatus={streaming.status}
                    runProgress={streaming.progress}
                    streamingLogs={streaming.streamingLogs}
                    buildLogs={buildLogs}
                    buildLogsLoading={buildLogsLoading}
                    buildLogsError={buildLogsError}
                    buildLogsDiagnostics={buildLogsDiagnostics}
                    isImageSizeLimitError={isImageSizeLimitError}
                    lastBuildAt={project?.last_build_at}
                    lastBuildId={project?.last_build_id}
                    onFetchBuildLogs={fetchBuildLogs}
                    onTriggerRebuild={triggerRebuild}
                    projectId={projectId}
                    userContext={{
                      id: user?.id || null,
                      email: user?.email || null,
                      displayName: profile?.display_name || null,
                      tier: subscriptionTier,
                      locale,
                      creditsAvailable: usageData?.credits?.totalRemaining ?? 0,
                      creditsLoaded: !usageLoading && usageData !== null,
                      creditCosts: modelCreditCosts,
                      maxPayloadBytes: MINIAPP_MAX_PAYLOAD_BYTES[subscriptionTier] ?? MINIAPP_MAX_PAYLOAD_BYTES.free,
                      embeddingBatchLimit: EMBEDDING_BATCH_LIMITS[subscriptionTier] ?? EMBEDDING_BATCH_LIMITS.free,
                    }}
                    familyConfig={project?.family_params ? {
                      params: project.family_params,
                      description: project.description,
                      title: project.name,
                    } : null}
                    onFrontendError={handleFrontendError}
                    appUrl={project.is_published && publicUrl ? publicUrl : window.location.origin + '/project/' + projectId}
                    sessionToken={session?.access_token}
                    getTurnstileToken={getTurnstileToken}
                    containerRef={previewCardRef}
                    projectUpdatedAt={project?.updated_at}
                  />
                  {uploadError && (
                    <div className="mt-2 flex items-center gap-2 text-theme-error text-sm">
                      <AlertCircle className="h-4 w-4" />{uploadError}
                    </div>
                  )}
                  {rebuildError && (
                    <div className="mt-2 flex items-center gap-2 text-theme-error text-sm">
                      <AlertCircle className="h-4 w-4" />Rebuild failed: {rebuildError}
                    </div>
                  )}
                  {projectId && project?.name && (
                    <div
                      className="absolute z-10"
                      style={{
                        bottom: `${reportButtonPos.bottom}px`,
                        right: `${reportButtonPos.right}px`,
                      }}
                    >
                      <ReportProblemButton projectId={projectId} projectName={project.name} />
                    </div>
                  )}
                </div>
              ) : (
                /* Placeholder when default HTML or no frontend */
                <div className="bg-theme-bg rounded-xl border border-theme-border-muted overflow-hidden flex-1">
                  <div className="flex items-center gap-2 px-3 py-2 bg-theme-surface border-b border-theme-border-muted">
                    <Sparkles className="h-4 w-4 text-accent-400" />
                    <span className="text-sm font-medium text-theme-text-secondary">Preview</span>
                  </div>
                  <div className="h-[400px] lg:h-full flex items-center justify-center">
                    <div className="text-center px-6 max-w-md">
                      <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-accent-500/20 to-cyan-500/20 mb-4">
                        <Wand2 className="h-7 w-7 text-accent-400" />
                      </div>
                      <p className="text-theme-text font-medium mb-2">Your app will appear here</p>
                      <p className="text-sm text-theme-text-muted">
                        Start by describing what you want to build in the chat panel. The preview will update automatically each time the AI makes changes.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Below-the-fold: Code editor, Conversations ── */}
        {builderMode && (
          <div className="mt-3 sm:mt-4 space-y-6 sm:space-y-8">

            {/* Code Editor (admin/premium) */}
            {(isAdmin || isPremiumUser) && (
              <div>
                <CodeEditor
                  projectId={projectId!}
                  onBuildTriggered={triggerRebuild}
                  onBuildStarted={() => setLocalBuildStatus('building')}
                  isForked={!!project.forked_from}
                />
              </div>
            )}

            {/* Conversations list */}
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                <h2 className="font-display text-lg sm:text-xl font-semibold text-theme-text">
                  Conversation History
                </h2>
                <button
                  onClick={handleCreateConversation}
                  disabled={creating}
                  className="btn btn-primary w-full sm:w-auto"
                >
                  {creating ? <LoadingSpinner size="sm" /> : (
                    <><Plus className="h-4 w-4" />New Conversation</>
                  )}
                </button>
              </div>

              {conversationsLoading ? (
                <div className="flex justify-center py-12"><LoadingSpinner /></div>
              ) : conversations.length === 0 ? (
                <div className="card p-8 text-center">
                  <MessageSquare className="h-8 w-8 text-theme-text-muted mx-auto mb-4" />
                  <p className="text-theme-text-muted text-sm">
                    No conversations yet. Use the chat panel above to start building.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {conversations.map((conversation, index) => (
                    <div
                      key={conversation.id}
                      className={
                        'card p-3 hover:border-theme-border-muted transition-all duration-200 flex items-center justify-between group cursor-pointer' +
                        (conversation.id === activeConversationId ? ' border-accent-500/50 bg-accent-500/5' : '')
                      }
                      style={{ animationDelay: index * 50 + 'ms' }}
                    >
                      <button
                        onClick={() => switchToConversation(conversation.id)}
                        className="flex items-center gap-3 flex-1 min-w-0 text-left"
                      >
                        <div className={'flex h-9 w-9 items-center justify-center rounded-lg transition-colors shrink-0 ' +
                          (conversation.id === activeConversationId
                            ? 'bg-accent-500/20'
                            : 'bg-theme-surface group-hover:bg-accent-500/20')
                        }>
                          <MessageSquare className={'h-4 w-4 transition-colors ' +
                            (conversation.id === activeConversationId
                              ? 'text-accent-400'
                              : 'text-theme-text-muted group-hover:text-accent-400')
                          } />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className={'font-medium truncate text-sm transition-colors ' +
                            (conversation.id === activeConversationId ? 'text-accent-400' : 'text-theme-text group-hover:text-accent-400')
                          }>
                            {conversation.title}
                          </h3>
                          <p className="text-xs text-theme-text-muted">
                            Last updated {formatDate(conversation.updated_at)}
                          </p>
                        </div>
                      </button>
                      <div className="flex items-center gap-1">
                        <Link
                          to={'/project/' + projectId + '/conversation/' + conversation.id}
                          className="p-2 rounded-lg text-theme-text-muted hover:text-theme-text hover:bg-theme-surface-hover transition-colors sm:opacity-0 sm:group-hover:opacity-100"
                          title="Open full conversation"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Link>
                        <button
                          onClick={() => setShowDeleteModal(conversation.id)}
                          className="p-2 rounded-lg text-theme-text-muted hover:text-theme-error hover:bg-theme-error-bg transition-colors sm:opacity-0 sm:group-hover:opacity-100"
                          aria-label="Delete conversation"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {canDeleteProject && (
          <div className="mt-10 border-t border-theme-border-muted pt-4 text-center">
            <button
              type="button"
              onClick={handleOpenProjectDeleteModal}
              className="text-sm text-theme-error hover:opacity-80 underline underline-offset-2 transition-opacity"
            >
              Delete this project
            </button>
          </div>
        )}
      </div>

      {/* ═══════════════════ MODALS ═══════════════════════════════════════ */}

      <Modal isOpen={showSettingsModal} onClose={closeSettingsModal} title="Mini-App settings" size="lg">
        <form onSubmit={handleSaveSettings} className="space-y-5">
          <div>
            <label htmlFor="settingsName" className="block text-sm font-medium text-theme-text-secondary mb-2">Mini-App name</label>
            <input id="settingsName" type="text" value={projectName} onChange={(e) => setProjectName(e.target.value)} className="input" required />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="settingsDescription" className="block text-sm font-medium text-theme-text-secondary">Description</label>
              <button
                type="button"
                onClick={generateDescription}
                disabled={generatingDescription || !frontendHtml}
                className="flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title={frontendHtml ? 'Generate description using AI' : 'No frontend code available'}
              >
                {generatingDescription ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                {generatingDescription ? 'Generating...' : 'Generate'}
              </button>
            </div>
            <textarea id="settingsDescription" value={projectDescription} onChange={(e) => setProjectDescription(e.target.value)} className="input min-h-[100px] resize-none" />
            <button
              type="button"
              onClick={() => setShowSpecificationModal(true)}
              className="mt-2 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 underline underline-offset-2"
            >
              Specification
            </button>
          </div>
          <div>
            <label htmlFor="settingsLogoEmoji" className="block text-sm font-medium text-theme-text-secondary mb-2">Logo emoji</label>
            <div className="space-y-3">
              {/* Header with Generate button */}
              <div className="flex items-center justify-between">
                <span className="text-xs text-theme-text-muted">
                  {projectLogoEmoji ? 'Current emoji' : 'No emoji selected'}
                </span>
                <button
                  type="button"
                  onClick={generateLogoEmoji}
                  disabled={generatingLogoEmoji}
                  className="flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {generatingLogoEmoji ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                  {generatingLogoEmoji ? 'Generating...' : 'Generate'}
                </button>
              </div>

              {/* Main Content Area */}
              <div className="p-4 rounded-lg bg-theme-surface/50 border border-theme-border-muted min-h-[100px] flex flex-col items-center justify-center gap-3 transition-all">
                {/* Logic: 
                    If choices exist -> Show choices (Selection State)
                    Else if emoji exists -> Show Big Emoji (Selected State)
                    Else -> Show "Generate" prompt (Empty State)
                 */}

                {logoEmojiChoices.length > 0 ? (
                  <div className="w-full animate-fade-in">
                    <p className="text-xs text-center text-theme-text-muted mb-3">Choose one to apply ({logoEmojiChoices.length} options — scroll to see more):</p>
                    <div className="overflow-x-auto pb-2 -mx-1 px-1" style={{ scrollbarWidth: 'thin' }}>
                      <div className="flex gap-3" style={{ width: 'max-content' }}>
                        {logoEmojiChoices.map((emoji) => (
                          <button
                            key={emoji}
                            type="button"
                            onClick={() => {
                              setProjectLogoEmoji(emoji);
                              setLogoEmojiChoices([]); // Clear choices to show "Selected" state
                            }}
                            className="h-12 w-12 flex-shrink-0 text-2xl flex items-center justify-center rounded-xl border border-theme-border-muted bg-theme-surface hover:border-theme-primary hover:bg-theme-primary/5 hover:scale-110 transition-all duration-200"
                            title={'Select ' + emoji}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : projectLogoEmoji ? (
                  <div className="text-center animate-fade-in">
                    <div className="h-20 w-20 mx-auto text-5xl flex items-center justify-center rounded-2xl bg-theme-surface border border-theme-border shadow-sm mb-2">
                      {projectLogoEmoji}
                    </div>
                    <p className="text-xs text-theme-success font-medium flex items-center justify-center gap-1">
                      <Check className="h-3 w-3" /> Selected
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-2 text-theme-text-muted animate-fade-in">
                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-theme-surface border border-theme-border-dashed mb-2 opacity-50">
                      <Sparkles className="h-5 w-5" />
                    </div>
                    <p className="text-sm">No emoji set</p>
                    <button
                      type="button"
                      onClick={generateLogoEmoji}
                      className="text-xs text-accent-500 hover:text-accent-600 hover:underline mt-1"
                    >
                      Generate options
                    </button>
                  </div>
                )}
              </div>
            </div>
            <p className="mt-1.5 text-xs text-theme-text-muted">Required for publishing.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-theme-text-secondary mb-2">Mini-App Type</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${effectiveProjectType === 'utility' ? 'bg-theme-primary/5 border-theme-primary' : 'bg-theme-surface/50 border-theme-border-muted hover:border-theme-border'
                } ${typeIsLocked ? 'opacity-70 cursor-not-allowed' : ''}`}>
                <input
                  type="radio"
                  name="miniappType"
                  value="utility"
                  checked={effectiveProjectType === 'utility'}
                  onChange={() => setProjectType('utility')}
                  className="mt-1"
                  disabled={typeIsLocked}
                />
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-theme-text">Utility</span>
                  <p className="text-sm text-theme-text-muted mt-1">Tools, productivity helpers, and practical workflows.</p>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${effectiveProjectType === 'game' ? 'bg-theme-primary/5 border-theme-primary' : 'bg-theme-surface/50 border-theme-border-muted hover:border-theme-border'
                } ${typeIsLocked ? 'opacity-70 cursor-not-allowed' : ''}`}>
                <input
                  type="radio"
                  name="miniappType"
                  value="game"
                  checked={effectiveProjectType === 'game'}
                  onChange={() => setProjectType('game')}
                  className="mt-1"
                  disabled={typeIsLocked}
                />
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-theme-text">Game</span>
                  <p className="text-sm text-theme-text-muted mt-1">Playful, interactive, or game-like experiences.</p>
                </div>
              </label>
            </div>
            {typeIsLocked && (
              <div className="mt-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-sm text-amber-700 dark:text-amber-300">
                An admin has locked the mini-app type to <strong className="font-semibold">{adminTypeOverride}</strong>. You can’t change it.
              </div>
            )}
          </div>
          <div className="flex items-center justify-between p-4 rounded-lg bg-theme-surface/50 border border-theme-border-muted">
            <div className="min-w-0 flex-1 mr-4">
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-theme-text-muted shrink-0" />
                <span className="font-medium text-theme-text">Publish mini-app</span>
              </div>
              <p className="text-sm text-theme-text-muted mt-1">Make this mini-app publicly accessible via URL</p>
            </div>
            <button
              type="button"
              onClick={async () => {
                if (isPublished) {
                  setIsPublished(false);
                  return;
                }

                const normalizedLogoEmoji = normalizeLogoEmoji(projectLogoEmoji);
                if (!isLogoEmojiSet(normalizedLogoEmoji)) {
                  showSettingsToast('Set the Logo emoji first before publishing this mini-app.', 'warning');
                  return;
                }

                setProjectLogoEmoji(normalizedLogoEmoji);
                if (isFreeUser && !canFreeUserPublish) {
                  setShowPublishUpgradeModal(true);
                  return;
                }
                setIsPublished(true);
              }}
              disabled={publishToggleDisabled}
              className={'relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ' +
                (isPublished ? 'bg-blue-500' : 'bg-zinc-200 dark:bg-zinc-700') +
                (publishToggleDisabled ? ' opacity-60 cursor-not-allowed' : '')
              }
            >
              <span className={'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ' +
                (isPublished ? 'translate-x-6' : 'translate-x-1')
              } />
            </button>
          </div>
          {isPublished && (
            <div className="space-y-3">
              {!isAdmin && !profile?.display_name && (
                <div>
                  <label htmlFor="settingsDisplayName" className="block text-sm font-medium text-theme-text-secondary mb-2">
                    Nickname <span className="text-theme-error">*</span>
                  </label>
                  <input
                    id="settingsDisplayName" type="text" value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="input" placeholder="Your public nickname" required maxLength={50}
                  />
                  <p className="mt-1.5 text-xs text-theme-text-muted">This will be shown as the author on your published apps.</p>
                </div>
              )}
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="h-4 w-4 text-theme-text-muted" />
                <span className="font-medium text-theme-text">Monetization Mode</span>
              </div>
              <label className={'flex items-start gap-3 p-4 rounded-lg border transition-colors ' +
                (monetizationMode === 'free'
                  ? 'bg-theme-primary/5 border-theme-primary'
                  : monetizationDisabled
                    ? 'bg-theme-surface/50 border-theme-border-muted'
                    : 'bg-theme-surface/50 border-theme-border-muted hover:border-theme-border') +
                (monetizationDisabled ? ' opacity-60 cursor-not-allowed' : ' cursor-pointer')
              }>
                <input
                  type="radio"
                  name="monetizationMode"
                  value="free"
                  checked={monetizationMode === 'free'}
                  onChange={() => setMonetizationMode('free')}
                  className="mt-1"
                  disabled={monetizationDisabled}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-theme-text-muted" />
                    <span className="font-medium text-theme-text">Free for all users</span>
                  </div>
                  <p className="text-sm text-theme-text-muted mt-1">Anyone can use this app. Usage is tracked but not monetized. No revenue sharing.</p>
                </div>
              </label>
              <label className={'flex items-start gap-3 p-4 rounded-lg border transition-colors ' +
                (monetizationMode === 'free_with_paid_features'
                  ? 'bg-theme-primary/5 border-theme-primary'
                  : monetizationDisabled
                    ? 'bg-theme-surface/50 border-theme-border-muted'
                    : 'bg-theme-surface/50 border-theme-border-muted hover:border-theme-border') +
                (monetizationDisabled ? ' opacity-60 cursor-not-allowed' : ' cursor-pointer')
              }>
                <input
                  type="radio"
                  name="monetizationMode"
                  value="free_with_paid_features"
                  checked={monetizationMode === 'free_with_paid_features'}
                  onChange={() => setMonetizationMode('free_with_paid_features')}
                  className="mt-1"
                  disabled={monetizationDisabled}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-amber-500" />
                    <span className="font-medium text-theme-text">Free with premium features</span>
                    <span className="text-xs bg-theme-primary/10 text-theme-primary px-2 py-0.5 rounded-full">Monetized</span>
                  </div>
                  <p className="text-sm text-theme-text-muted mt-1">Free users can use basic features. Paid users get premium features. Revenue sharing enabled.</p>
                </div>
              </label>
              <label className={'flex items-start gap-3 p-4 rounded-lg border transition-colors ' +
                (monetizationMode === 'paid_only'
                  ? 'bg-theme-primary/5 border-theme-primary'
                  : monetizationDisabled
                    ? 'bg-theme-surface/50 border-theme-border-muted'
                    : 'bg-theme-surface/50 border-theme-border-muted hover:border-theme-border') +
                (monetizationDisabled ? ' opacity-60 cursor-not-allowed' : ' cursor-pointer')
              }>
                <input
                  type="radio"
                  name="monetizationMode"
                  value="paid_only"
                  checked={monetizationMode === 'paid_only'}
                  onChange={() => setMonetizationMode('paid_only')}
                  className="mt-1"
                  disabled={monetizationDisabled}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Crown className="h-4 w-4 text-amber-500" />
                    <span className="font-medium text-theme-text">Paid subscribers only</span>
                    <span className="text-xs bg-theme-primary/10 text-theme-primary px-2 py-0.5 rounded-full">Monetized</span>
                  </div>
                  <p className="text-sm text-theme-text-muted mt-1">Only paid subscribers can use this app. Free users will be prompted to subscribe. Revenue sharing enabled.</p>
                </div>
              </label>
              {monetizationDisabled && (
                <p className="text-xs text-theme-text-muted">
                  Monetization options are available on paid plans.
                </p>
              )}
              {detectRequiresAuth(codeFiles) && (
                <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
                    <ShieldCheck className="h-4 w-4" />
                    <span>This app uses Storage or Vector APIs and requires user authentication.</span>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Family mini-app info (read-only) */}
          {(project?.is_family_template || project?.family_params) && (
            <div className="rounded-lg border border-theme-border-muted bg-theme-surface/50 p-4 space-y-2">
              <div className="flex items-center gap-2 mb-3">
                <GitFork className="h-4 w-4 text-theme-text-muted" />
                <span className="text-sm font-medium text-theme-text">
                  {project?.is_family_template ? 'Family Template' : 'Family Fork'}
                </span>
              </div>
              {project?.is_family_template && project?.family_creation_prompt && (
                <div className="text-xs">
                  <span className="text-theme-text-muted">Creation prompt: </span>
                  <span className="text-theme-text">{project.family_creation_prompt}</span>
                </div>
              )}
              {project?.family_params && Object.entries(project.family_params).map(([paramName, paramValue]) => (
                <div key={paramName} className="text-xs">
                  <span className="text-theme-text-muted">{paramName.replace(/_/g, ' ')}: </span>
                  <span className="text-theme-text font-mono">{paramValue}</span>
                </div>
              ))}
              {project?.family_config_ref && (
                <div className="text-xs">
                  <span className="text-theme-text-muted">Config ref: </span>
                  <span className="text-theme-text font-mono">{project.family_config_ref.substring(0, 12)}…</span>
                </div>
              )}
              {project?.is_family_customized && (
                <div className="text-xs text-theme-warning flex items-center gap-1 mt-1">
                  <AlertCircle className="h-3 w-3" />
                  Code has been customized (won't be overwritten on re-generation)
                </div>
              )}
            </div>
          )}
          <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
            <button type="button" onClick={closeSettingsModal} className="btn btn-secondary flex-1">Cancel</button>
            <button type="submit" disabled={saving} className="btn btn-primary flex-1">
              {saving ? <LoadingSpinner size="sm" /> : 'Save changes'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={showSpecificationModal}
        onClose={() => setShowSpecificationModal(false)}
        title="Mini-App Specification"
        size="lg"
      >
        <div className="space-y-4">
          <p className="text-xs text-theme-text-muted">
            This specification is auto-generated from the latest mini-app code and should stay focused on user experience.
          </p>
          <textarea
            id="settingsSpecification"
            value={projectSpecification}
            onChange={(e) => setProjectSpecification(e.target.value)}
            className="input min-h-[420px] resize-y"
            placeholder="Specification will appear here after the first generated version."
          />
          <div className="flex justify-end">
            <button type="button" onClick={() => setShowSpecificationModal(false)} className="btn btn-secondary">
              Done
            </button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={showStatsModal} onClose={() => setShowStatsModal(false)} title="Usage Statistics" size="lg">
        <p className="text-sm text-theme-text-muted mb-4">Last 7 days</p>
        {costStatsLoading ? (
          <div className="flex justify-center py-8"><LoadingSpinner size="sm" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-4 rounded-lg bg-theme-surface border border-theme-border-muted">
                <div className="text-2xl font-bold text-theme-text">{runCostStats?.totalRuns ?? 0}</div>
                <div className="text-xs text-theme-text-muted mt-1">Total Runs</div>
              </div>
              <div className="p-4 rounded-lg bg-theme-surface border border-theme-border-muted">
                <div className="text-2xl font-bold text-theme-text">{runCostStats?.paidUserRuns ?? 0}</div>
                <div className="text-xs text-theme-text-muted mt-1">Paid Runs</div>
              </div>
              {runCostStats?.isMonetized ? (
                <>
                  <div className="p-4 rounded-lg bg-theme-surface border border-theme-border-muted">
                    <div className="text-2xl font-bold text-theme-text">
                      ${parseFloat(runCostStats?.totalCostUsd ?? '0').toFixed(6)}
                    </div>
                    <div className="text-xs text-theme-text-muted mt-1">Total Cost (30d)</div>
                  </div>
                  <div className="p-4 rounded-lg bg-theme-surface border border-theme-border-muted">
                    <div className="text-2xl font-bold text-theme-text">
                      ${parseFloat(runCostStats?.avgCostPerRun ?? '0').toFixed(8)}
                    </div>
                    <div className="text-xs text-theme-text-muted mt-1">Avg Cost/Run</div>
                  </div>
                </>
              ) : (
                <div className="p-4 rounded-lg bg-theme-surface border border-theme-border-muted col-span-2">
                  <div className="flex items-center gap-2 text-2xl font-bold text-theme-text">
                    <Clock className="h-5 w-5" />
                    {(parseFloat(runCostStats?.avgRuntimeMs ?? '0') / 1000).toFixed(2)}s
                  </div>
                  <div className="text-xs text-theme-text-muted mt-1">Avg Runtime</div>
                </div>
              )}
            </div>
            <p className="mt-4 text-xs text-theme-text-muted">
              {runCostStats?.isMonetized
                ? 'Cost calculated using Cloud Run pricing. Revenue sharing is enabled for monetized apps.'
                : 'This app is not monetized. Enable monetization in settings to see cost statistics and enable revenue sharing.'}
            </p>
          </>
        )}
      </Modal>

      <Modal isOpen={!!showDeleteModal} onClose={() => setShowDeleteModal(null)} title="Delete conversation" size="sm">
        <p className="text-theme-text-muted mb-6">
          Are you sure you want to delete this conversation? All messages will be permanently lost.
        </p>
        <div className="flex flex-col-reverse sm:flex-row gap-3">
          <button onClick={() => setShowDeleteModal(null)} className="btn btn-secondary flex-1">Cancel</button>
          <button
            onClick={() => showDeleteModal && handleDeleteConversation(showDeleteModal)}
            disabled={deleting}
            className="btn btn-danger flex-1"
          >
            {deleting ? <LoadingSpinner size="sm" /> : 'Delete'}
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={showProjectDeleteModal}
        onClose={() => {
          if (deletingProject) return;
          setShowProjectDeleteModal(false);
          setDeleteProjectError(null);
        }}
        title="Delete project"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-theme-text-muted">
            This will permanently delete the project, conversations, code files, versions, and backend infrastructure.
          </p>
          {isFamilyForkProject && (
            <label className="flex items-start gap-2 rounded-lg border border-theme-border-muted bg-theme-surface/50 p-3 text-sm text-theme-text">
              <input
                type="checkbox"
                checked={deleteFamilyRecord}
                onChange={(e) => setDeleteFamilyRecord(e.target.checked)}
                disabled={deletingProject}
                className="mt-0.5"
              />
              <span>
                Also delete this fork&apos;s combination from the parent <code className="text-xs">family.json</code>.
              </span>
            </label>
          )}
          {deleteProjectError && (
            <div className="rounded-lg border border-theme-error-border bg-theme-error-bg px-3 py-2 text-sm text-theme-error">
              {deleteProjectError}
            </div>
          )}
          <div className="flex flex-col-reverse sm:flex-row gap-3">
            <button
              onClick={() => {
                if (deletingProject) return;
                setShowProjectDeleteModal(false);
                setDeleteProjectError(null);
              }}
              className="btn btn-secondary flex-1"
              disabled={deletingProject}
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteProject}
              disabled={deletingProject}
              className="btn btn-danger flex-1"
            >
              {deletingProject ? <LoadingSpinner size="sm" /> : 'Delete project'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={showPublishUpgradeModal} onClose={() => setShowPublishUpgradeModal(false)} title="Upgrade to Publish More Mini-Apps" size="sm">
        <div className="text-center">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-amber-500/20 to-orange-500/20 mb-4">
            <Crown className="h-7 w-7 text-amber-500" />
          </div>
          <p className="text-theme-text-secondary mb-2">Free plans can publish one mini-app.</p>
          <p className="text-sm text-theme-text-muted mb-6">
            {PAYMENTS_DISABLED
              ? PAYMENTS_DISABLED_MESSAGE
              : 'Upgrade to the Standard plan to publish more mini-apps, share them with the world, and earn revenue when other users use your public apps.'}
          </p>
          <div className="flex flex-col-reverse sm:flex-row gap-3">
            <button onClick={() => setShowPublishUpgradeModal(false)} className="btn btn-secondary flex-1">Maybe Later</button>
            {!PAYMENTS_DISABLED && (
              <Link to="/settings" onClick={() => setShowPublishUpgradeModal(false)} className="btn btn-primary flex-1">View Plans</Link>
            )}
          </div>
        </div>
      </Modal>

      <Modal isOpen={showErrorModal} onClose={handleDismissError} title={pendingErrorType === 'build' ? 'Build Error' : 'Execution Error'} size="sm">
        <div className="text-center">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-theme-error-bg mb-4">
            <AlertCircle className="h-7 w-7 text-theme-error" />
          </div>
          <p className="text-theme-text-secondary mb-2">
            {pendingErrorType === 'build'
              ? 'Your mini-app backend failed to build.'
              : 'Your mini-app encountered an error while running.'}
          </p>
          <p className="text-sm text-theme-text-muted mb-6">
            Would you like to send this error to the coding AI so it can help fix the issue?
          </p>
          <div className="flex flex-col-reverse sm:flex-row gap-3">
            <button onClick={handleDismissError} className="btn btn-secondary flex-1">Dismiss</button>
            <button onClick={handleGoToConversation} disabled={creating || sending} className="btn btn-primary flex-1">
              {(creating || sending) ? <LoadingSpinner size="sm" /> : 'Fix with AI'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={showImageSizeLimitModal} onClose={handleDismissImageSizeLimitError} title="Docker Image Size Limit Exceeded" size="md">
        <div className="text-center">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-theme-error-bg mb-4">
            <AlertCircle className="h-7 w-7 text-theme-error" />
          </div>
          <p className="text-theme-text-secondary mb-2">
            {hasUnlimitedImageSize
              ? 'Your Docker image exceeds the size limit.'
              : `Your Docker image exceeds the ${imageSizeLimitLabel} size limit.`}
          </p>
          <p className="text-sm text-theme-text-muted mb-4">
            The deployment failed because the container image is too large.
          </p>
          <div className="text-left bg-theme-surface-active rounded-lg p-4 mb-6">
            <p className="text-xs font-medium text-theme-text-muted mb-2">Common solutions:</p>
            <ul className="text-xs text-theme-text-muted space-y-1">
              <li>• Use a slim base image (e.g., python:3.11-slim)</li>
              <li>• Remove unnecessary dependencies</li>
              <li>• Use multi-stage builds</li>
              <li>• Clean pip cache with --no-cache-dir</li>
              {!hasUnlimitedImageSize && subscriptionTier === 'free' && (
                <li>• Upgrade to Standard (1GB) or Pro (2GB) for larger limits</li>
              )}
              {!hasUnlimitedImageSize && (subscriptionTier === 'standard' || subscriptionTier === 'standard_with_ads') && (
                <li>• Upgrade to Pro for 2GB limit</li>
              )}
            </ul>
          </div>
          <p className="text-sm text-theme-text-muted mb-6">
            Would you like to open a conversation with the coding AI to help reduce the image size?
          </p>
          <div className="flex flex-col-reverse sm:flex-row gap-3">
            <button onClick={handleDismissImageSizeLimitError} className="btn btn-secondary flex-1">Dismiss</button>
            <button onClick={handleFixImageSizeLimitError} disabled={creating} className="btn btn-primary flex-1">
              {creating ? <LoadingSpinner size="sm" /> : 'Fix with AI'}
            </button>
          </div>
        </div>
      </Modal>

      {settingsToast && (
        <div className="fixed top-4 right-4 z-[90]">
          <div
            className={'max-w-[min(92vw,420px)] rounded-lg border px-4 py-2 text-sm shadow-lg backdrop-blur-sm ' +
              (settingsToast.tone === 'success'
                ? 'border-theme-success/40 bg-theme-success-bg text-theme-success'
                : settingsToast.tone === 'error'
                  ? 'border-theme-error-border bg-theme-error-bg text-theme-error'
                  : 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300')
            }
          >
            {settingsToast.message}
          </div>
        </div>
      )}

      <TurnstileWidget />

      {/* Insufficient Credits Modal */}
      <InsufficientCreditsModal
        isOpen={showCreditsModal}
        onClose={() => setShowCreditsModal(false)}
        isAuthenticated={!!user}
        creditsRemaining={usageData?.credits?.totalRemaining ?? 0}
        avgCreditsNeeded={avgDailyCredits ?? undefined}
      />
    </Layout>
  );
}
